import React, { useEffect, useState, useRef, useContext } from 'react';
import axios from 'axios';
import { phoneLocalDigits } from '../components/UI/PhoneInput';
import { apiUrl } from '../config/api';
import PersonalInfoForm from '../components/Forms/PersonalInfoForm';
import OccupationInfoForm from '../components/Forms/OccupationInfoForm';
import BankInfoForm from '../components/Forms/BankInfoForm';
import LoanForm from '../components/Forms/LoanForm';
import ResponsiveTable from '../components/ResponsiveTable';
import StatusBadge from '../components/UI/StatusBadge';
import AccordionSection from '../components/UI/AccordionSection';
import Stepper from '../components/UI/Stepper';
import ContractPreview from '../components/ContractPreview';
import PreQualification from '../components/PreQualification';
import Button from '../components/UI/Button';
import ActionButton from '../components/UI/ActionButton';
import FullScreenPanel from '../components/UI/FullScreenPanel';
import { submitLoanWithContract } from '../utils/LoanAgreementUtils';
import { creditReportError } from '../components/affordability/creditReportView';
import { DEFAULT_EXPENSE_CODES } from '../utils/loanExpenseUtils';
import KpiCard from '../components/Admin/KpiCard';
import ManageLoans from './ManageLoans';
import { clientsMetrics, toCurrency } from '../utils/adminMetrics';
import { exportToCsv } from '../utils/adminExport';
import { AuthContext } from '../context/AuthContext';
import { auditModalView } from '../utils/auditClient';
import DocumentsStrip from '../components/DocumentsStrip';
import EntityAuditTrail from '../components/EntityAuditTrail';
import { useEnvironment } from '../hooks/useEnvironment';
import { formatDateDMY } from '../utils/dateUtils';
import Pager from '../components/UI/Pager';
import DocumentTile, { DOCUMENT_GRID } from '../components/UI/DocumentTile';
import { Sparkles, CheckCircle2, X, CreditCard, ChevronDown, ChevronRight, PenLine, Search, ArrowLeft, User, FileText } from 'lucide-react';

const PAGE_SIZE = 10;

// What the workspace's document row is for: proof of who the client is, and
// what the loan they are on now was assessed against. An older loan's
// paperwork stays on that loan, in its own details panel.
const CLIENT_DOC_TYPES = ['Identification', 'Proof Of Residence'];
const LATEST_LOAN_DOC_TYPES = ['Payslip', 'Bank Statement'];

// Matched on the whole type, not a substring: "Proof Of Residence" contains
// "id" (res-id-ence), so a loose test quietly pulls in the wrong documents.
const isDocType = (doc, types) =>
  types.some((t) => t.toLowerCase() === String(doc?.document_type || '').trim().toLowerCase());

// Mirrors the states the table paints in its "Active" column. The server
// applies these across the whole match, so the count and pager stay honest.
const STATUS_FILTERS = [
  { label: 'All statuses', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Awaiting update', value: 'awaiting_update' },
  { label: 'Resubmitted', value: 'resubmitted' },
];

const TRAINABLE_FIELD_OPTIONS = [
  { key: 'email', label: 'Personal: Email' },
  { key: 'first_name', label: 'Personal: First Name' },
  { key: 'middle_name', label: 'Personal: Middle Name' },
  { key: 'last_name', label: 'Personal: Last Name' },
  { key: 'gender', label: 'Personal: Gender' },
  { key: 'id_passport_no', label: 'Personal: ID/Passport No' },
  { key: 'phone_number', label: 'Personal: Phone Number' },
  { key: 'postal_address', label: 'Personal: Postal Address' },
  { key: 'erf_no', label: 'Personal: ERF No' },
  { key: 'street_name', label: 'Personal: Street Name' },
  { key: 'suburb', label: 'Personal: Suburb' },
  { key: 'city', label: 'Personal: City' },
  { key: 'country', label: 'Personal: Country' },
  { key: 'marital_status', label: 'Personal: Marital Status' },
  { key: 'occupation', label: 'Occupation: Occupation' },
  { key: 'salary', label: 'Occupation: Salary' },
  { key: 'employer_name', label: 'Occupation: Employer Name' },
  { key: 'employer_phone', label: 'Occupation: Employer Phone' },
  { key: 'employer_address', label: 'Occupation: Employer Address' },
  { key: 'payslip_number', label: 'Occupation: Payslip Number' },
  { key: 'bank_name', label: 'Bank: Bank Name' },
  { key: 'branch_code', label: 'Bank: Branch Code' },
  { key: 'account_number', label: 'Bank: Account Number' },
  { key: 'account_type', label: 'Bank: Account Type' },
];

const TRAINABLE_FIELD_LABELS = TRAINABLE_FIELD_OPTIONS.reduce((acc, option) => {
  acc[option.key] = option.label;
  return acc;
}, {});

const ONBOARDING_DOCUMENT_DEFS = [
  { key: 'identificationFile', label: 'Identification Document' },
  { key: 'proofOfResidenceFile', label: 'Proof Of Residence' },
  { key: 'bankStatementFile', label: 'Bank Statement' },
  { key: 'payslipFile', label: 'Payslip' },
];

const AddIcon = ({ onClick, label }) => (
  <Button
    type="button"
    onClick={onClick}
    variant="primary"
    size="sm"
    className="ml-2 flex items-center gap-1"
    title={label}
    icon={
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path d="M12 4v16m8-8H4" />
      </svg>
    }
  >
    {label}
  </Button>
);

const LocalDocumentPreview = ({ file }) => {
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    if (!file) {
      setPreviewUrl('');
      return;
    }

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  if (!file) {
    return (
      <div className="h-80 md:h-[34rem] rounded-lg border border-dashed border-gray-300 dark:border-slate-600 flex items-center justify-center text-xs text-gray-500 dark:text-slate-400 bg-white dark:bg-slate-900/30">
        Upload and select a document to preview.
      </div>
    );
  }

  if (String(file.type || '').startsWith('image/')) {
    return (
      <div className="rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900/30 p-2 h-80 md:h-[34rem] overflow-auto">
        <img src={previewUrl} alt={file.name} className="max-w-full h-auto rounded" />
      </div>
    );
  }

  if (file.type === 'application/pdf') {
    return (
      <div className="rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900/30 h-80 md:h-[34rem] overflow-hidden">
        <iframe title={file.name} src={previewUrl} className="w-full h-full" />
      </div>
    );
  }

  return (
    <div className="h-80 md:h-[34rem] rounded-lg border border-gray-200 dark:border-slate-600 flex items-center justify-center text-xs text-gray-500 dark:text-slate-400 bg-white dark:bg-slate-900/30 px-3 text-center">
      Preview is not available for this file type.
    </div>
  );
};

const ManageClients = () => {
  // The same reading of a number as the phone field itself (+264…, 081…,
  // 264…): mobiles have 9 digits after the code, landlines 8, and some
  // other countries 10.
  const validatePhone9 = (value) => {
    const n = phoneLocalDigits(value).length;
    return n >= 8 && n <= 10;
  };
  const { user } = useContext(AuthContext);
  const env = useEnvironment();
  // AI document extraction calls an external LLM; it's turned off in the public
  // demo workspace (which resets/sandboxes data and shares an API quota).
  const isDemo = !!env?.demo;
  const aiEnabled = env?.aiEnabled !== false;
  const hasPerm = (p) => Array.isArray(user?.permissions) && (user.permissions.includes('*') || user.permissions.includes(p));
  const canManageClients = hasPerm('ManageClients') || hasPerm('ManageConfig');
  const canApproveClient = hasPerm('approve_client');
  const canManageLoans = hasPerm('ManageLoans') || hasPerm('EditLoan') || hasPerm('ApproveLoans') || hasPerm('EnterRepayment');
  const canForceSubmitClient = hasPerm('force_submit');
  // Same gate the Loans route carried before it moved in here.
  const canViewLoans = hasPerm('ViewLoans') || canManageLoans;
  const [clients, setClients] = useState([]);
  const [clientLimitStatus, setClientLimitStatus] = useState(null);
  const [clientsTotal, setClientsTotal] = useState(0);
  const [clientsDbMetrics, setClientsDbMetrics] = useState(null);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [formData, setFormData] = useState({});
  const [message, setMessage] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [confirmAction, setConfirmAction] = useState(null);
  // "Return for update" modal: { clientId, name } + note text + busy flag.
  const [returnModal, setReturnModal] = useState(null);
  const [returnNote, setReturnNote] = useState('');
  const [returningClient, setReturningClient] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // What is typed in the search box, as opposed to the term actually submitted.
  const [searchInput, setSearchInput] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  // The client + loans workspace that a picked client opens into.
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  // Bumped after a loan is created so the loans panel reloads.
  const [loansRefreshKey, setLoansRefreshKey] = useState(0);
  // Guards the "single match opens straight away" rule against re-firing.
  const autoOpenedRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [idFile, setIdFile] = useState(null);
  const [porFile, setPorFile] = useState(null);
  const [onboardingBankStatementFile, setOnboardingBankStatementFile] = useState(null);
  const [onboardingPayslipFile, setOnboardingPayslipFile] = useState(null);
  const [isExtractingOnboardingDocs, setIsExtractingOnboardingDocs] = useState(false);
  const [onboardingExtractionMessage, setOnboardingExtractionMessage] = useState('');
  const [onboardingExtractionWarnings, setOnboardingExtractionWarnings] = useState([]);
  const [isLoadingDocumentCandidates, setIsLoadingDocumentCandidates] = useState(false);
  const [documentCandidates, setDocumentCandidates] = useState([]);
  const [geminiUsage, setGeminiUsage] = useState(null);
  const [selectedTrainingDocumentKey, setSelectedTrainingDocumentKey] = useState('');
  const [trainingTargetField, setTrainingTargetField] = useState('');
  const [showTrainingMode, setShowTrainingMode] = useState(false);
  // How the staff member wants to capture a new client: 'ai' shows the
  // AI Auto-Fill panel above the form; 'manual' hides it entirely.
  const [addClientMode, setAddClientMode] = useState('ai');
  const [showAddModeChooser, setShowAddModeChooser] = useState(false);
  const [manualTrainingHints, setManualTrainingHints] = useState({});
  const [idFileUrl, setIdFileUrl] = useState('');
  const [showOccupationDropdown, setShowOccupationDropdown] = useState(false);
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [occupationForm, setOccupationForm] = useState({});
  const [bankForm, setBankForm] = useState({});
  const [occupationFormErrors, setOccupationFormErrors] = useState({});
  const [bankFormErrors, setBankFormErrors] = useState({});
  const [modalMessage, setModalMessage] = useState('');
  const [openAccordion, setOpenAccordion] = useState('');
  const [openDetailsAccordion, setOpenDetailsAccordion] = useState('');
  // Active tab in the client view/edit panel (replaces the accordion sections).
  const [activeClientTab, setActiveClientTab] = useState('personal');
  const [loadingSection, setLoadingSection] = useState('');
  const [sectionMessages, setSectionMessages] = useState({});
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [isAddingClient, setIsAddingClient] = useState(false);
  const [addClientMessage, setAddClientMessage] = useState('');
  const [documentSubmissionGuard, setDocumentSubmissionGuard] = useState(null);
  // Override prompt shown when document-correspondence check blocks the submit
  // and the user has force_submit permission. Carries the formatted lines to
  // render in the modal body.
  const [mismatchOverridePrompt, setMismatchOverridePrompt] = useState(null);
  const [showClientLoadingModal, setShowClientLoadingModal] = useState(false);
  const [clientLoadingText, setClientLoadingText] = useState('Loading client profile...');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsClient, setDetailsClient] = useState(null);
  const [addStep, setAddStep] = useState(0);
  const idFileInputRef = useRef();
  const [isLoadingClients, setIsLoadingClients] = useState(true);

  // Loan modal state
  const [showLoanModal, setShowLoanModal] = useState(false);
  const [loanForm, setLoanForm] = useState({});
  const [loanFormErrors, setLoanFormErrors] = useState({});
  const [loanMessage, setLoanMessage] = useState('');
  const [loanClient, setLoanClient] = useState(null);
  // Expenses chosen for the loan being added, and the active types used to
  // price them for the contract preview. Starts at the two applied by default.
  const [loanExpenseCodes, setLoanExpenseCodes] = useState(DEFAULT_EXPENSE_CODES);
  const [addLoanPhase, setAddLoanPhase] = useState('prequalify');
  const [loanTermMonths, setLoanTermMonths] = useState(null);
  // How this loan is being captured: 'ai' runs pre-qualification first and
  // takes the documents there, 'manual' goes straight to the form. Mirrors the
  // Add Client chooser.
  const [addLoanMode, setAddLoanMode] = useState('ai');
  const [showAddLoanModeChooser, setShowAddLoanModeChooser] = useState(false);
  const [loanAssessment, setLoanAssessment] = useState(null);
  const [loanPreqKey, setLoanPreqKey] = useState(0);
  // This staff member's saved application for the client in the add-loan
  // window (/api/loan-drafts/client/:id), if there is one.
  const [loanDraft, setLoanDraft] = useState(null);
  const [loanDraftBusy, setLoanDraftBusy] = useState(false);
  const [canAddLoan, setCanAddLoan] = useState(true);
  const [loanStatusMessage, setLoanStatusMessage] = useState('');
  const [loanBannerMessage, setLoanBannerMessage] = useState('');
  const [interestRates, setInterestRates] = useState([]);
  const [bankStatementFile, setBankStatementFile] = useState(null);
  const [payslipFile, setPayslipFile] = useState(null);
  // The client's credit report and its date, when no pre-qualification holds them.
  const [bureauFile, setBureauFile] = useState(null);
  const [bureauDate, setBureauDate] = useState('');
  // Note: clientPhotoFile is not needed for admin/staff loan applications
  const bankStatementInputRef = useRef();
  const payslipInputRef = useRef();

  // Client loans state
  const [clientLoans, setClientLoans] = useState([]);
  // The newest loan and its documents, shown in the workspace's document row.
  const [latestLoan, setLatestLoan] = useState(null);
  const [latestLoanDocs, setLatestLoanDocs] = useState([]);
  const [clientOnlyDocs, setClientOnlyDocs] = useState([]);
  const [loadingTopDocs, setLoadingTopDocs] = useState(false);
  const [loadingLoans, setLoadingLoans] = useState(false);

  // Deduction day state
  const [deductionDay, setDeductionDay] = useState('');
  
  // Contract related states - simplified
  const [showContractModal, setShowContractModal] = useState(false);
  const [isGeneratingContract, setIsGeneratingContract] = useState(false);
  const [contractUrl, setContractUrl] = useState('');
  
  const actionsPopupRef = useRef(null);

  const updateFormData = (nextValue) => {
    setDocumentSubmissionGuard(null);
    setFormData(prev => (typeof nextValue === 'function' ? nextValue(prev) : nextValue));
  };

  const fetchGeminiUsage = async () => {
    if (!aiEnabled) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(apiUrl('/api/clients/gemini-usage'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeminiUsage(res.data);
    } catch {
      // non-critical — silently ignore
    }
  };

  // Fetch Gemini usage on mount so the quota bar is visible from the start
  useEffect(() => { if (env && aiEnabled) fetchGeminiUsage(); }, [env, aiEnabled]); // eslint-disable-line

  // Fetch interest rates on mount
  useEffect(() => {
    const fetchRates = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl('/api/interestrates'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInterestRates(res.data.filter(r => r.is_active));
      } catch {}
    };
    fetchRates();
  }, []);

  // Nothing is listed until a search is submitted — the page opens on the
  // search area, not on a client list.
  useEffect(() => {
    if (!searchTerm) {
      setClients([]);
      setClientsTotal(0);
      return;
    }
    fetchClients(currentPage, searchTerm, statusFilter);
  }, [currentPage, searchTerm, statusFilter]);

  // The client limit banner is about the whole book, so it loads on its own.
  useEffect(() => {
    fetchClientLimitStatus();
  }, []);

  useEffect(() => {
    // KPI cards should be based on DB totals, not the current table page.
    const fetchMetrics = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(
          apiUrl('/api/clients/metrics'),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setClientsDbMetrics(res.data || null);
      } catch {
        setClientsDbMetrics(null);
      }
    };
    fetchMetrics();
  }, []);

  const fetchClientLimitStatus = async (tokenOverride) => {
    try {
      const token = tokenOverride || localStorage.getItem('token');
      const res = await axios.get(apiUrl('/api/clients/limit-status'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientLimitStatus(res.data);
    } catch {
      // silent: limit status is optional
      setClientLimitStatus(null);
    }
  };

  const fetchClients = async (page = currentPage, term = searchTerm, status = statusFilter) => {
    setIsLoadingClients(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        apiUrl(`/api/clients?page=${page}&pageSize=${PAGE_SIZE}&search=${encodeURIComponent(term || '')}&status=${encodeURIComponent(status || 'all')}`),
        {
        headers: { Authorization: `Bearer ${token}` }
        }
      );
      const { data = [], total = 0 } = res.data || {};
      const clientsData = (Array.isArray(data) ? data : []).map(client => ({ ...client }));
      setClients(clientsData);
      setClientsTotal(Number(total) || 0);
      setError(null);
      fetchClientLimitStatus(token);
    } catch (err) {
      setError('Failed to fetch clients');
      setClientsTotal(0);
    } finally {
      setIsLoadingClients(false);
    }
  };

  const validatePersonalSection = () => {
    let valid = true;
    const errors = {};
    
    // Validate required text fields
    [
      ['email', 'Email'],
      ['first_name', 'First name'],
      ['last_name', 'Last name'],
      ['id_passport_no', 'ID/Passport No'],
      ['phone_number', 'Phone Number'],
      ['postal_address', 'Postal Address'],
      ['marital_status', 'Marital Status'],
      ['gender', 'Gender'],
    ].forEach(([field, label]) => {
      if (!formData[field] || !formData[field].trim()) {
        errors[field] = `${label} is required`;
        valid = false;
      }
    });

    if (formData.phone_number && !validatePhone9(formData.phone_number)) {
      errors.phone_number = 'Enter the full phone number (8 to 10 digits after the country code)';
      valid = false;
    }
    
    // File validation logic - split into distinct cases
    if (!selectedClient) {
      // CASE 1: New client - file is required
      if (!idFile) {
        errors.idFile = 'ID/Passport file is required';
        valid = false;
      } else {
        const okIdType = idFile.type === 'application/pdf';
        if (!okIdType) {
          errors.idFile = 'ID/Passport file must be a PDF';
          valid = false;
        }
      }
      // Proof Of Residence is mandatory for new client (PDF)
      if (!porFile) {
        errors.porFile = 'Proof Of Residence is required';
        valid = false;
      } else {
        const okType = porFile.type === 'application/pdf';
        if (!okType) {
          errors.porFile = 'Proof Of Residence must be a PDF';
          valid = false;
        }
      }
    } else {
      // CASE 2: Existing client - file is optional
      // Only validate if a new file was actually selected
      if (idFile) {
        const okIdType = idFile.type === 'application/pdf';
        if (!okIdType) {
          errors.idFile = 'ID/Passport file must be a PDF';
          valid = false;
        }
      }
      // If no new file is selected, it's valid (keep existing file)
    }
    
    setFormErrors(errors);
    return valid;
  };

  const validateOccupationSection = () => {
    let valid = true;
    const errors = {};
    [
      ['occupation', 'Occupation'],
      ['salary', 'Salary'],
      ['employer_name', 'Employer Name'],
      ['employer_phone', 'Employer Phone'],
      ['employer_address', 'Employer Address'],
      ['payslip_number', 'Payslip Number'],
    ].forEach(([field, label]) => {
      if (field === 'salary') {
        const s = formData.salary;
        if (s === undefined || s === null || String(s).trim() === '') {
          errors.salary = `${label} is required`;
          valid = false;
        }
        return;
      }

      if (!formData[field] || !String(formData[field]).trim()) {
        errors[field] = `${label} is required`;
        valid = false;
      }
    });

    if (formData.salary && isNaN(Number(formData.salary))) {
      errors.salary = 'Salary must be a number';
      valid = false;
    }

    if (formData.employer_phone && !validatePhone9(formData.employer_phone)) {
      errors.employer_phone = 'Enter the full employer phone number (8 to 10 digits after the country code)';
      valid = false;
    }
    setFormErrors(errors);
    return valid;
  };

  const validateBankSection = () => {
    let valid = true;
    const errors = {};
    [
      ['bank_name', 'Bank Name'],
      ['branch_code', 'Branch Code'],
      ['account_number', 'Account Number'],
      ['account_type', 'Account Type'],
    ].forEach(([field, label]) => {
      if (!formData[field] || !formData[field].trim()) {
        errors[field] = `${label} is required`;
        valid = false;
      }
    });
    setFormErrors(errors);
    return valid;
  };

  const mergeExtractedOnboardingData = (payload = {}) => {
    const merged = {
      ...(payload.personal_info || {}),
      ...(payload.occupation_info || {}),
      ...(payload.bank_info || {}),
    };

    setDocumentSubmissionGuard(null);
    setFormData(prev => {
      const next = { ...(prev || {}) };
      Object.entries(merged).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        const text = String(value).trim();
        if (!text) return;
        next[key] = text;
      });
      return next;
    });
  };

  const formatOnboardingCheckDate = (value) => {
    const text = String(value || '').trim();
    if (!text) return '';
    const date = new Date(text);
    if (Number.isNaN(date.getTime())) return text;
    return formatDateDMY(text);
  };

  const buildPayslipExtractionSummary = (documentChecks = {}) => {
    const status = String(documentChecks?.payslip_recency_status || '').trim().toLowerCase();
    if (!status) return '';

    const issueDate = formatOnboardingCheckDate(documentChecks?.payslip_issue_date);
    const datedPrefix = issueDate ? `Payslip dated ${issueDate}` : 'Payslip';

    if (status === 'valid') {
      return `${datedPrefix} passed the latest-payslip check.`;
    }

    if (status === 'stale') {
      return `${datedPrefix} is older than one month.`;
    }

    if (status === 'unknown') {
      return documentChecks?.payslip_recency_note || 'Payslip date could not be confirmed.';
    }

    return '';
  };

  const onboardingFilesByField = {
    identificationFile: idFile,
    proofOfResidenceFile: porFile,
    bankStatementFile: onboardingBankStatementFile,
    payslipFile: onboardingPayslipFile,
  };

  // Pass a fieldKey to clear only that document's candidates (e.g. when a single file changes).
  // Omit fieldKey (or pass null) to clear all candidates.
  const clearLoadedDocumentCandidates = (fieldKey = null) => {
    setDocumentSubmissionGuard(null);
    if (fieldKey) {
      setDocumentCandidates(prev => (prev || []).filter(doc => doc.field !== fieldKey));
      // Reset training document selection if the cleared field was selected
      setSelectedTrainingDocumentKey(prev => (prev === fieldKey ? '' : prev));
    } else {
      setDocumentCandidates([]);
      setSelectedTrainingDocumentKey('');
    }
  };

  const getMissingOnboardingDocuments = () => ONBOARDING_DOCUMENT_DEFS
    .filter(({ key }) => !onboardingFilesByField[key])
    .map(({ label }) => label);

  const handleTrainingFieldFocus = (field) => {
    if (!TRAINABLE_FIELD_LABELS[field]) return;
    setTrainingTargetField(field);
  };

  const applyCandidateToTrainingTarget = (candidateValue) => {
    const value = String(candidateValue || '').trim();
    if (!value) return;

    if (!trainingTargetField) {
      setOnboardingExtractionMessage('Select or focus a target form field first, then click a document value.');
      return;
    }

    setDocumentSubmissionGuard(null);
    setFormData(prev => ({ ...(prev || {}), [trainingTargetField]: value }));
    setFormErrors(prev => {
      if (!prev || !Object.prototype.hasOwnProperty.call(prev, trainingTargetField)) {
        return prev;
      }
      return { ...prev, [trainingTargetField]: null };
    });
    setManualTrainingHints(prev => ({ ...(prev || {}), [trainingTargetField]: value }));
    setOnboardingExtractionMessage(
      `Applied "${value}" to ${TRAINABLE_FIELD_LABELS[trainingTargetField]}. This mapping will be reused as an extraction hint.`
    );
  };

  // Single combined call: extracts structured form data AND loads click-fill candidates
  // for all uploaded documents in one round-trip to reduce API token usage.
  const handleExtractAndLoadAll = async () => {
    const missingDocs = getMissingOnboardingDocuments();

    if (missingDocs.length > 0) {
      setOnboardingExtractionMessage(`Upload all onboarding documents before extraction. Missing: ${missingDocs.join(', ')}`);
      return;
    }

    setDocumentSubmissionGuard(null);
    setIsExtractingOnboardingDocs(true);
    setIsLoadingDocumentCandidates(true);
    setOnboardingExtractionWarnings([]);
    setOnboardingExtractionMessage('');

    try {
      const token = localStorage.getItem('token');
      const extractionForm = new FormData();

      if (idFile) extractionForm.append('identificationFile', idFile);
      if (porFile) extractionForm.append('proofOfResidenceFile', porFile);
      if (onboardingBankStatementFile) extractionForm.append('bankStatementFile', onboardingBankStatementFile);
      if (onboardingPayslipFile) extractionForm.append('payslipFile', onboardingPayslipFile);
      if (Object.keys(manualTrainingHints).length > 0) {
        extractionForm.append('manualHints', JSON.stringify(manualTrainingHints));
      }

      const response = await axios.post(
        apiUrl('/api/clients/extract-all'),
        extractionForm,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Auto-fill form fields from structured extraction
      const extractedData = response?.data?.data || {};
      mergeExtractedOnboardingData(extractedData);

      const notes = Array.isArray(extractedData.notes)
        ? extractedData.notes.filter(Boolean)
        : [];
      setOnboardingExtractionWarnings(notes);

      // Replace candidates with fresh results keyed to the currently uploaded files.
      // This ensures no stale candidates from previously loaded documents remain.
      const docs = Array.isArray(response?.data?.documents) ? response.data.documents : [];
      setDocumentCandidates(docs);

      // Auto-select the training document matching the current selection, or the first
      // document that has candidates so the panel is immediately usable.
      if (docs.length > 0) {
        const currentSelectionStillValid = docs.some(
          d => d.field === selectedTrainingDocumentKey && d.candidates?.length > 0
        );
        if (!currentSelectionStillValid) {
          const firstWithCandidates = docs.find(d => d.candidates?.length > 0);
          setSelectedTrainingDocumentKey(firstWithCandidates?.field || '');
        }
      }

      const confidenceSuffix = extractedData?.confidence
        ? ` Confidence: ${extractedData.confidence}.`
        : '';
      const payslipSummary = buildPayslipExtractionSummary(extractedData?.document_checks);
      const payslipSuffix = payslipSummary ? ` ${payslipSummary}` : '';
      const hintCount = Object.keys(manualTrainingHints).length;
      const hintSuffix = hintCount > 0 ? ` ${hintCount} manual hint${hintCount > 1 ? 's were' : ' was'} applied.` : '';
      const candidateCount = docs.reduce((sum, d) => sum + (d.candidates?.length || 0), 0);
      const candidateSuffix = candidateCount > 0 ? ` ${candidateCount} click-fill values loaded.` : '';
      setOnboardingExtractionMessage(
        `Extraction complete. Form fields auto-filled.${confidenceSuffix}${hintSuffix}${candidateSuffix}${payslipSuffix} Review values before saving.`
      );
      fetchGeminiUsage();
    } catch (err) {
      const apiMessage =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to extract data from documents.';
      setOnboardingExtractionMessage(apiMessage);
    } finally {
      setIsExtractingOnboardingDocs(false);
      setIsLoadingDocumentCandidates(false);
    }
  };

  const onboardingTrainingDocuments = ONBOARDING_DOCUMENT_DEFS.map(def => {
    const doc = documentCandidates.find(item => item.field === def.key);
    return {
      ...def,
      file: onboardingFilesByField[def.key] || null,
      candidates: Array.isArray(doc?.candidates) ? doc.candidates : [],
    };
  });

  const selectedTrainingDocument = onboardingTrainingDocuments.find(
    doc => doc.key === selectedTrainingDocumentKey && doc.file
  ) || onboardingTrainingDocuments.find(doc => doc.file) || null;

  const selectedTrainingCandidates = Array.isArray(selectedTrainingDocument?.candidates)
    ? selectedTrainingDocument.candidates
    : [];

  const hintCount = Object.keys(manualTrainingHints).length;
  const currentTargetFieldValue = trainingTargetField ? String(formData?.[trainingTargetField] || '').trim() : '';
  const documentGuardHasOverride = Boolean(documentSubmissionGuard?.canForceSubmit && canForceSubmitClient);
  const documentSubmissionGuardPanel = documentSubmissionGuard ? (
    <div className="rounded-lg border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="font-semibold text-amber-800 dark:text-amber-200">Document correspondence check blocked this submission.</p>
          <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
            {documentSubmissionGuard.message || documentSubmissionGuard?.report?.summary || 'Some uploaded documents do not appear to belong to the same client.'}
          </p>
        </div>
        {documentGuardHasOverride && (
          <span className="text-[11px] px-2 py-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 border border-amber-300 dark:border-amber-700">
            Force Submit available
          </span>
        )}
      </div>

      {Array.isArray(documentSubmissionGuard?.report?.mismatchDocuments) && documentSubmissionGuard.report.mismatchDocuments.length > 0 && (
        <div className="mt-3 space-y-1">
          <p className="text-xs font-semibold text-amber-800 dark:text-amber-200">Blocking documents</p>
          {documentSubmissionGuard.report.mismatchDocuments.map((doc, idx) => (
            <div key={`${doc.label || 'document'}-${idx}`} className="text-xs text-amber-700 dark:text-amber-300">
              {doc.label}: {(doc.mismatchedFields || []).map(field => field.label).join(', ') || 'Details unavailable'}
            </div>
          ))}
        </div>
      )}

      {Array.isArray(documentSubmissionGuard?.report?.crossDocumentConflicts) && documentSubmissionGuard.report.crossDocumentConflicts.length > 0 && (
        <div className="mt-3 space-y-1">
          <p className="text-xs font-semibold text-amber-800 dark:text-amber-200">Cross-document conflicts</p>
          {documentSubmissionGuard.report.crossDocumentConflicts.map((conflict, idx) => (
            <div key={`${conflict.leftDocument?.label || 'left'}-${conflict.rightDocument?.label || 'right'}-${idx}`} className="text-xs text-amber-700 dark:text-amber-300">
              {conflict.leftDocument?.label} vs {conflict.rightDocument?.label}: {(conflict.mismatchedFields || []).map(field => field.label).join(', ') || 'Details unavailable'}
            </div>
          ))}
        </div>
      )}

      {Array.isArray(documentSubmissionGuard?.report?.reviewDocuments) && documentSubmissionGuard.report.reviewDocuments.length > 0 && (
        <div className="mt-3 space-y-1">
          <p className="text-xs font-semibold text-amber-800 dark:text-amber-200">Manual review required</p>
          {documentSubmissionGuard.report.reviewDocuments.map((doc, idx) => (
            <div key={`${doc.label || 'review'}-${idx}`} className="text-xs text-amber-700 dark:text-amber-300">
              {doc.label}: {Array.isArray(doc.notes) && doc.notes.length > 0 ? doc.notes.join(' ') : 'Verification details unavailable'}
            </div>
          ))}
        </div>
      )}
    </div>
  ) : null;

  const onboardingUploadTiles = [
    { key: 'identificationFile', type: 'Identification', hint: 'ID or passport', file: idFile, setFile: setIdFile, errorKey: 'idFile' },
    { key: 'proofOfResidenceFile', type: 'Proof of Residence', hint: 'Utility bill or lease', file: porFile, setFile: setPorFile, errorKey: 'porFile' },
    { key: 'bankStatementFile', type: 'Bank Statement', hint: 'Last 3 months', file: onboardingBankStatementFile, setFile: setOnboardingBankStatementFile },
    { key: 'payslipFile', type: 'Payslip', hint: 'Latest month', file: onboardingPayslipFile, setFile: setOnboardingPayslipFile },
  ];
  const onboardingUploadedCount = onboardingUploadTiles.filter(t => t.file).length;

  const onboardingAutoFillPanel = (
    <div className="rounded-xl border border-blue-100 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm overflow-hidden">
      <div className="h-0.5 bg-gradient-to-r from-blue-500 to-indigo-500" />
      <div className="p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center flex-shrink-0">
            <Sparkles size={18} />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-slate-100">AI Auto-Fill</h4>
            <p className="text-[11px] text-gray-500 dark:text-slate-400">Upload onboarding documents and let AI fill in the form for you</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleExtractAndLoadAll}
          disabled={isDemo || isExtractingOnboardingDocs || isLoadingDocumentCandidates}
          title={isDemo ? 'AI auto-fill is disabled in the demo environment.' : undefined}
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition ${
            isDemo
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-slate-700 dark:text-slate-500'
              : (isExtractingOnboardingDocs || isLoadingDocumentCandidates)
              ? 'bg-blue-200 text-blue-700 cursor-wait dark:bg-blue-900/50 dark:text-blue-300'
              : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
          }`}
        >
          {(isExtractingOnboardingDocs || isLoadingDocumentCandidates) ? (
            <span className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-blue-400 border-t-transparent" />
          ) : (
            <Sparkles size={14} />
          )}
          {isDemo ? 'Disabled in demo' : (isExtractingOnboardingDocs || isLoadingDocumentCandidates) ? 'Extracting…' : 'Extract & Auto-Fill All'}
        </button>
      </div>

      {(() => {
        const extractionSucceeded = !!onboardingExtractionMessage && !/failed|error/i.test(onboardingExtractionMessage);
        const currentStep = extractionSucceeded ? 3 : (onboardingUploadedCount > 0 ? 2 : 1);
        const steps = [
          { n: 1, text: `Upload Documents${onboardingUploadedCount ? ` (${onboardingUploadedCount}/4)` : ''}` },
          { n: 2, text: 'Extract & Auto-Fill' },
          { n: 3, text: 'Review Filled Fields' },
        ];
        return (
          <div className="flex items-center gap-1.5 sm:gap-2 mt-4">
            {steps.map((step, i) => (
              <React.Fragment key={step.n}>
                {i > 0 && <ChevronRight size={16} className="flex-shrink-0 text-gray-300 dark:text-slate-600" />}
                <div
                  className={`flex-1 text-center px-2 sm:px-3 py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-colors ${
                    currentStep === step.n
                      ? 'bg-blue-600 text-white shadow-sm'
                      : currentStep > step.n
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800/40'
                      : 'bg-gray-100 text-gray-500 border border-gray-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600'
                  }`}
                >
                  {currentStep > step.n && <CheckCircle2 size={13} className="inline mr-1 -mt-0.5" />}
                  {step.text}
                </div>
              </React.Fragment>
            ))}
          </div>
        );
      })()}

      {isDemo && (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
          <strong>AI auto-fill is turned off in the demo environment.</strong> It sends uploaded documents to an external AI service and draws on a shared API quota, so it is disabled in this public sandbox (where data is reset periodically). It is fully available in a real installation.
        </p>
      )}

      {geminiUsage && (() => {
        const pct = geminiUsage.limit > 0 ? Math.min(100, (geminiUsage.used / geminiUsage.limit) * 100) : 0;
        const isLow = geminiUsage.remaining <= Math.ceil(geminiUsage.limit * 0.1);
        const isEmpty = geminiUsage.remaining === 0;
        const barColor = isEmpty
          ? 'bg-red-500'
          : isLow
          ? 'bg-amber-400'
          : 'bg-emerald-500';
        const textColor = isEmpty
          ? 'text-red-600 dark:text-red-400'
          : isLow
          ? 'text-amber-600 dark:text-amber-400'
          : 'text-gray-500 dark:text-slate-400';
        return (
          <div className="mt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] text-gray-500 dark:text-slate-400 font-medium">
                Google API calls today ({formatDateDMY(geminiUsage.date)})
              </span>
              <span className={`text-[11px] font-semibold ${textColor}`}>
                {geminiUsage.used.toLocaleString()} used &nbsp;·&nbsp; {geminiUsage.remaining.toLocaleString()} left / {geminiUsage.limit.toLocaleString()}
              </span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-gray-200 dark:bg-slate-700 overflow-hidden">
              <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
            </div>
            {isEmpty && (
              <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">Daily limit reached. Extraction will fail until midnight UTC.</p>
            )}
            {isLow && !isEmpty && (
              <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">Approaching daily limit — use extractions carefully.</p>
            )}
          </div>
        );
      })()}

      {hintCount > 0 && (
        <p className="mt-1 text-xs text-blue-700 dark:text-blue-300">
          Manual hints ready: {hintCount}. They will be sent with the next extraction.
        </p>
      )}

      <div className={`${DOCUMENT_GRID} mt-4`}>
        {onboardingUploadTiles.map(({ key, type, hint, file, setFile, errorKey }) => {
          // A new file: the details read from the old one no longer apply.
          const choose = (f) => {
            setFile(f);
            clearLoadedDocumentCandidates(key);
            setOnboardingExtractionMessage('');
            setOnboardingExtractionWarnings([]);
          };
          return (
            <div key={key}>
              <DocumentTile type={type} hint={hint} file={file} onPick={choose} onRemove={() => choose(null)} required={Boolean(errorKey)} />
              {errorKey && formErrors[errorKey] && <p className="text-red-500 text-xs mt-1">{formErrors[errorKey]}</p>}
            </div>
          );
        })}
      </div>

      {onboardingExtractionMessage && (
        <div
          className={`mt-3 rounded-lg border px-3 py-2 text-xs ${onboardingExtractionMessage.toLowerCase().includes('failed') || onboardingExtractionMessage.toLowerCase().includes('error') ? 'bg-red-50 border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-800/40 dark:text-red-300' : 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-800/40 dark:text-emerald-300'}`}
        >
          {onboardingExtractionMessage}
        </div>
      )}

      {onboardingExtractionWarnings.length > 0 && (
        <div className="mt-3 rounded-lg border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-900/20 p-3">
          <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-1">Extraction notes</p>
          <ul className="list-disc pl-4 space-y-1 text-xs text-amber-700 dark:text-amber-300">
            {onboardingExtractionWarnings.map((note, idx) => (
              <li key={`${note}-${idx}`}>{note}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-gray-100 dark:border-slate-700">
        <button
          type="button"
          onClick={() => setShowTrainingMode(v => !v)}
          className="w-full flex items-center justify-between gap-2 text-left rounded-lg -mx-1 px-1 py-1 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
        >
          <div>
            <h5 className="text-xs font-semibold text-gray-700 dark:text-slate-200">
              Field Training Mode
              <span className="ml-1.5 text-[10px] font-medium text-gray-400 dark:text-slate-500">advanced</span>
              {hintCount > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">{hintCount} hint{hintCount === 1 ? '' : 's'}</span>
              )}
            </h5>
            <p className="text-[11px] text-gray-500 dark:text-slate-400">Manually pick values from a document to fill specific fields</p>
          </div>
          <ChevronDown size={16} className={`flex-shrink-0 text-gray-400 transition-transform duration-200 ${showTrainingMode ? 'rotate-180' : ''}`} />
        </button>

        {showTrainingMode && (
        <>
        {hintCount > 0 && (
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => setManualTrainingHints({})}
              className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300"
            >
              Clear Manual Hints
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">Target Form Field</label>
            <select
              value={trainingTargetField}
              onChange={(e) => setTrainingTargetField(e.target.value)}
              className="w-full p-2.5"
            >
              <option value="">Select field (or focus an input)</option>
              {TRAINABLE_FIELD_OPTIONS.map(option => (
                <option key={option.key} value={option.key}>{option.label}</option>
              ))}
            </select>
            {trainingTargetField && (
              <p className="mt-1 text-xs text-gray-600 dark:text-slate-400">
                Current value: {currentTargetFieldValue || 'Empty'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">Training Document</label>
            <select
              value={selectedTrainingDocument?.key || ''}
              onChange={(e) => setSelectedTrainingDocumentKey(e.target.value)}
              className="w-full p-2.5"
            >
              <option value="">Select uploaded document</option>
              {onboardingTrainingDocuments.filter(doc => doc.file).map(doc => (
                <option key={doc.key} value={doc.key}>
                  {doc.label}{doc.candidates.length ? ` (${doc.candidates.length} values)` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-col gap-3 mt-3">
          <div className="rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900/30 overflow-hidden flex flex-col">
            <div className="px-3 py-2 border-b border-gray-200 dark:border-slate-600">
              <p className="text-xs font-semibold text-gray-700 dark:text-slate-200">
                {selectedTrainingDocument ? `${selectedTrainingDocument.label} Candidate Values` : 'Candidate Values'}
              </p>
              <p className="text-[11px] text-gray-500 dark:text-slate-400">
                {selectedTrainingCandidates.length > 0
                  ? 'Click a value to apply it to the selected target field.'
                  : 'Choose a training document, then load its values to see clickable candidates.'}
              </p>
            </div>
            <div className="p-2.5 max-h-48 overflow-auto flex flex-wrap gap-1.5">
              {selectedTrainingCandidates.length > 0 ? (
                selectedTrainingCandidates.map((candidate, idx) => {
                  const candidateText = String(candidate || '').trim();
                  const isActive = currentTargetFieldValue && currentTargetFieldValue === candidateText;
                  return (
                    <button
                      type="button"
                      key={`${candidateText}-${idx}`}
                      onClick={() => applyCandidateToTrainingTarget(candidateText)}
                      className={`px-2.5 py-1 rounded-full text-xs border transition ${isActive ? 'bg-blue-100 border-blue-300 text-blue-800 dark:bg-blue-900/40 dark:border-blue-700 dark:text-blue-200' : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-blue-50 hover:border-blue-200 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-700'}`}
                    >
                      {candidateText}
                    </button>
                  );
                })
              ) : (
                <p className="text-xs text-gray-500 dark:text-slate-400 px-1 py-2">
                  No values loaded yet for this document.
                </p>
              )}
            </div>
          </div>

          <LocalDocumentPreview file={selectedTrainingDocument?.file || null} />
        </div>
        </>
        )}
      </div>
      </div>
    </div>
  );

  const handleUpdatePersonal = async () => {
    // Force clear any file errors BEFORE validation runs
    setFormErrors(prevErrors => ({ ...prevErrors, idFile: null }));
    
    // Run validation
    if (!validatePersonalSection()) return;
    
    setLoadingSection('personal');
    setSectionMessages({});
    
    try {
      const token = localStorage.getItem('token');
      
      // Use a different approach based on whether a new file is selected
      if (idFile || porFile) {
        // If a new file is selected, use FormData for multipart/form-data
        const formDataObj = new FormData();
        
        // Add all personal information fields
        Object.entries({
          first_name: formData.first_name || '',
          middle_name: formData.middle_name || '',
          last_name: formData.last_name || '',
          gender: formData.gender || '',
          id_passport_no: formData.id_passport_no || '',
          postal_address: formData.postal_address || '',
          erf_no: formData.erf_no || '',
          street_name: formData.street_name || '',
          suburb: formData.suburb || '',
          city: formData.city || '',
          country: formData.country || '',
          residential_address: formData.residential_address || '',
          phone_number: formData.phone_number || '',
          marital_status: formData.marital_status || '',
          email: formData.email || ''
        }).forEach(([key, value]) => {
          formDataObj.append(key, value);
        });
        
  // Add the files if provided
  if (idFile) formDataObj.append('identificationFile', idFile);
  if (porFile) formDataObj.append('proofOfResidenceFile', porFile);
        
        const response = await axios.put(
          apiUrl(`/api/clients/${selectedClient.personal_info.id}/update-id`),
          formDataObj,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        setSectionMessages({ personal: response.data.message || 'Personal info and file updated successfully' });
        
        // Reset file input
  setIdFile(null);
  setPorFile(null);
        if (idFileInputRef.current) idFileInputRef.current.value = '';
        
      } else {
        // No new file selected - use regular JSON for personal info only
        const response = await axios.put(
          apiUrl(`/api/clients/${selectedClient.personal_info.id}`),
          {
            first_name: formData.first_name || '',
            middle_name: formData.middle_name || '',
            last_name: formData.last_name || '',
            gender: formData.gender || '',
            id_passport_no: formData.id_passport_no || '',
            postal_address: formData.postal_address || '',
            erf_no: formData.erf_no || '',
            street_name: formData.street_name || '',
            suburb: formData.suburb || '',
            city: formData.city || '',
            country: formData.country || '',
            residential_address: formData.residential_address || '',
            phone_number: formData.phone_number || '',
            marital_status: formData.marital_status || '',
            email: formData.email || ''
          },
          {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        setSectionMessages({ personal: response.data.message || 'Personal info updated successfully' });
      }
      
      // Refresh client data
      fetchClients();
      
    } catch (err) {
      console.error('Update error:', err);
      
      // Better error handling to capture more detailed error messages
      let errorMessage = 'Failed to update personal info';
      
      if (err.response) {
        // The server responded with an error status
        errorMessage = err.response.data?.error || err.response.data?.message || errorMessage;
        console.log('Error response:', err.response.data);
      } else if (err.request) {
        // The request was made but no response was received
        errorMessage = 'No response from server. Please check your connection.';
      } else {
        // Something happened in setting up the request
        errorMessage = err.message || errorMessage;
      }
      
      setSectionMessages({ personal: errorMessage });
    }
    
    setLoadingSection('');
  };

  const handleUpdateOccupation = async () => {
    if (!validateOccupationSection()) return;
    setLoadingSection('occupation');
    setSectionMessages({});
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        apiUrl(`/api/clients/${selectedClient.personal_info.id}/occupation`),
        {
          occupation: formData.occupation,
          salary: formData.salary,
          employer_name: formData.employer_name,
          employer_phone: formData.employer_phone,
          employer_address: formData.employer_address,
          payslip_number: formData.payslip_number,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSectionMessages({ occupation: 'Occupation info updated successfully' });
      fetchClients();
    } catch (err) {
      setSectionMessages({ occupation: 'Failed to update occupation info' });
    }
    setLoadingSection('');
  };

  const handleUpdateBank = async () => {
    if (!validateBankSection()) return;
    setLoadingSection('bank');
    setSectionMessages({});
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        apiUrl(`/api/clients/${selectedClient.personal_info.id}/bank`),
        {
          bank_name: formData.bank_name,
          branch_code: formData.branch_code,
          account_number: formData.account_number,
          account_type: formData.account_type,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSectionMessages({ bank: 'Bank info updated successfully' });
      fetchClients();
    } catch (err) {
      setSectionMessages({ bank: 'Failed to update bank info' });
    }
    setLoadingSection('');
  };

  const handleAddClient = async (options = {}) => {
    const forceSubmit = Boolean(options?.forceSubmit);
    setAddClientMessage('');
    setDocumentSubmissionGuard(null);

    if (!validatePersonalSection()) {
      setAddStep(0);
      setAddClientMessage('Complete all required Personal Info fields before submitting.');
      return;
    }

    if (!validateOccupationSection()) {
      setAddStep(1);
      setAddClientMessage('Complete all required Occupation Info fields before submitting.');
      return;
    }

    if (!validateBankSection()) {
      setAddStep(2);
      setAddClientMessage('Complete all required Bank Info fields before submitting.');
      return;
    }

    setIsAddingClient(true);
    try {
      const token = localStorage.getItem('token');
      const email = (formData.email || '').trim();
      const idPassport = (formData.id_passport_no || '').trim();

      if (email || idPassport) {
        setIsCheckingDuplicate(true);
        try {
          await axios.post(
            apiUrl('/api/clients/check-exists'),
            { email, id_passport_no: idPassport },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (err) {
          const apiMessage =
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            'Client already exists.';
          setAddClientMessage(apiMessage);
          return;
        } finally {
          setIsCheckingDuplicate(false);
        }
      }

      const form = new FormData();
      Object.entries(formData).forEach(([key, value]) => form.append(key, value));
      if (idFile) form.append('identificationFile', idFile);
      if (porFile) form.append('proofOfResidenceFile', porFile);
      if (onboardingBankStatementFile) form.append('bankStatementFile', onboardingBankStatementFile);
      if (onboardingPayslipFile) form.append('payslipFile', onboardingPayslipFile);
      if (forceSubmit) form.append('forceSubmit', 'true');
      await axios.post(apiUrl('/api/clients'), form, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setShowModal(false);
      setFormData({});
      setFormErrors({});
      setIdFile(null);
      setPorFile(null);
      setOnboardingBankStatementFile(null);
      setOnboardingPayslipFile(null);
      setOnboardingExtractionMessage('');
      setOnboardingExtractionWarnings([]);
      setDocumentSubmissionGuard(null);
      setAddClientMessage('');
      setMessage(forceSubmit ? 'Client added successfully via Force Submit' : 'Client added successfully');
      fetchClients();
    } catch (err) {
      const errorCode = err?.response?.data?.code;
      const limitReachedMessage = errorCode === 'CLIENT_LIMIT_REACHED' ? 'Reached client limit' : null;
      const apiMessage =
        limitReachedMessage ||
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to add client';

      if (errorCode === 'DOCUMENT_CORRESPONDENCE_BLOCKED' || errorCode === 'DOCUMENT_CORRESPONDENCE_CHECK_UNAVAILABLE') {
        const correspondenceReport = err?.response?.data?.correspondenceReport;
        const fallbackReviewDocuments = err?.response?.data?.details
          ? [{ label: 'Automatic Verification', notes: [err.response.data.details] }]
          : [];
        const reportForState = correspondenceReport || {
          summary: apiMessage,
          mismatchDocuments: [],
          reviewDocuments: fallbackReviewDocuments,
          crossDocumentConflicts: [],
        };
        const canForceSubmitFromBackend = Boolean(err?.response?.data?.canForceSubmit);
        setDocumentSubmissionGuard({
          code: errorCode,
          message: apiMessage,
          canForceSubmit: canForceSubmitFromBackend,
          report: reportForState,
        });

        // Prompt-and-retry: when the user has force-submit permission and
        // hasn't already retried via Force Submit, surface an in-page modal
        // listing what failed and offer to add the client anyway.
        if (!forceSubmit && canForceSubmitFromBackend && canForceSubmitClient) {
          const mismatchLabels = (reportForState.mismatchDocuments || [])
            .map((doc) => doc.label || doc.name)
            .filter(Boolean);
          const reviewLabels = (reportForState.reviewDocuments || [])
            .map((doc) => doc.label || doc.name)
            .filter(Boolean);
          setMismatchOverridePrompt({
            mismatchLabels,
            reviewLabels,
            summary: reportForState.summary || apiMessage,
          });
        }
      }

      setAddClientMessage(apiMessage);
      setMessage(apiMessage);
    } finally {
      setIsCheckingDuplicate(false);
      setIsAddingClient(false);
    }
  };

  const handleAddOccupation = async (e) => {
    e.preventDefault();
    if (!selectedClient) return;
    let valid = true;
    const errors = {};
    [
      ['occupation', 'Occupation'],
      ['salary', 'Salary'],
      ['employer_name', 'Employer Name'],
      ['employer_phone', 'Employer Phone'],
      ['employer_address', 'Employer Address'],
      ['payslip_number', 'Payslip Number'],
    ].forEach(([field, label]) => {
      if (!occupationForm[field] || !String(occupationForm[field]).trim()) {
        errors[field] = `${label} is required`;
        valid = false;
      }
    });
    if (occupationForm.salary && isNaN(Number(occupationForm.salary))) {
      errors.salary = 'Salary must be a number';
      valid = false;
    }
    setOccupationFormErrors(errors);
    if (!valid) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        apiUrl(`/api/clients/${selectedClient.personal_info.id}/occupation`),
        occupationForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setModalMessage('New occupation info added and old deactivated.');
      setShowOccupationDropdown(false);
      setOccupationForm({});
      fetchClients();
    } catch (err) {
      setModalMessage('Failed to add new occupation info');
    }
  };

  const handleAddBank = async (e) => {
    e.preventDefault();
    if (!selectedClient) return;
    let valid = true;
    const errors = {};
    [
      ['bank_name', 'Bank Name'],
      ['branch_code', 'Branch Code'],
      ['account_number', 'Account Number'],
      ['account_type', 'Account Type'],
    ].forEach(([field, label]) => {
      if (!bankForm[field] || !bankForm[field].trim()) {
        errors[field] = `${label} is required`;
        valid = false;
      }
    });
    setBankFormErrors(errors);
    if (!valid) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        apiUrl(`/api/clients/${selectedClient.personal_info.id}/bank`),
        bankForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setModalMessage('New bank info added and old deactivated.');
      setShowBankDropdown(false);
      setBankForm({});
      fetchClients();
    } catch (err) {
      setModalMessage('Failed to add new bank info');
    }
  };

  const handleDeactivateClient = async (clientId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(apiUrl(`/api/clients/${clientId}/deactivate`), {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage('Client deactivated successfully');
      setConfirmAction(null);
      fetchClients();
    } catch (err) {
      setMessage('Failed to deactivate client');
    }
  };

  const handleActivateClient = async (clientId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(apiUrl(`/api/clients/${clientId}/activate`), {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage('Client activated successfully');
      setConfirmAction(null);
      fetchClients();
    } catch (err) {
      setMessage('Failed to activate client');
    }
  };

  const requestClientAction = (clientId, action, name) => {
    setConfirmAction({ clientId, action, name });
  };

  const openReturnForUpdate = (clientId, name) => {
    setReturnNote('');
    setReturnModal({ clientId, name });
  };

  const handleReturnForUpdate = async () => {
    if (!returnModal) return;
    const note = returnNote.trim();
    if (!note) { setMessage('Please add a note describing what needs to be updated.'); return; }
    setReturningClient(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        apiUrl(`/api/clients/${returnModal.clientId}/return-for-update`),
        { note },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage(res.data?.message || 'Registration returned to the client for update.');
      setReturnModal(null);
      setReturnNote('');
      fetchClients();
    } catch (err) {
      setMessage(err?.response?.data?.error || 'Failed to return registration for update.');
    } finally {
      setReturningClient(false);
    }
  };

  const openModal = async (client = null) => {
    let fullClient = client;
    try {
      const clientId = client?.personal_info?.id;
      if (clientId) {
        setClientLoadingText('Loading client profile...');
        setShowClientLoadingModal(true);
        const token = localStorage.getItem('token');
        const profileRes = await axios.get(apiUrl(`/api/clients/${clientId}/profile`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        fullClient = {
          ...client,
          ...(profileRes.data || {}),
          user_account: client?.user_account ?? null,
          loanStatusMessage: client?.loanStatusMessage ?? null,
        };
      }
    } catch {
      // If profile fetch fails, fall back to whatever we have
    } finally {
      if (client?.personal_info?.id) setShowClientLoadingModal(false);
    }

    setSelectedClient(fullClient);
    setFormData(
      fullClient
        ? {
            email: fullClient.personal_info?.email || '',
            first_name: fullClient.personal_info?.first_name || '',
            middle_name: fullClient.personal_info?.middle_name || '',
            last_name: fullClient.personal_info?.last_name || '',
            gender: fullClient.personal_info?.gender || '',
            id_passport_no: fullClient.personal_info?.id_passport_no || '',
            postal_address: fullClient.personal_info?.postal_address || '',
            erf_no: fullClient.personal_info?.erf_no || '',
            street_name: fullClient.personal_info?.street_name || '',
            suburb: fullClient.personal_info?.suburb || '',
            city: fullClient.personal_info?.city || '',
            country: fullClient.personal_info?.country || '',
            residential_address: fullClient.personal_info?.residential_address || '',
            phone_number: fullClient.personal_info?.phone_number || '',
            marital_status: fullClient.personal_info?.marital_status || '',
            occupation: fullClient.occupation_info?.occupation || '',
            salary: fullClient.occupation_info?.salary ?? '',
            employer_name: fullClient.occupation_info?.employer_name || '',
            employer_phone: fullClient.occupation_info?.employer_phone || '',
            employer_address: fullClient.occupation_info?.employer_address || '',
            payslip_number: fullClient.occupation_info?.payslip_number || '',
            bank_name: fullClient.bank_info?.bank_name || '',
            branch_code: fullClient.bank_info?.branch_code || '',
            account_number: fullClient.bank_info?.account_number || '',
            account_type: fullClient.bank_info?.account_type || '',
          }
        : {}
    );
    setMessage('');
    setFormErrors({});
    setIdFile(null);
  setPorFile(null);
    setOnboardingBankStatementFile(null);
    setOnboardingPayslipFile(null);
    setOnboardingExtractionMessage('');
    setOnboardingExtractionWarnings([]);
    setDocumentSubmissionGuard(null);
    setIsLoadingDocumentCandidates(false);
    setDocumentCandidates([]);
    setSelectedTrainingDocumentKey('');
    setTrainingTargetField('');
    setManualTrainingHints({});
    setIdFileUrl('');
    setShowOccupationDropdown(false);
    setShowBankDropdown(false);
    setOccupationForm({});
    setBankForm({});
    setOccupationFormErrors({});
    setBankFormErrors({});
    setModalMessage('');
    setOpenAccordion('');
    setLoadingSection('');
    setSectionMessages({});
    setAddStep(0);
    if (idFileInputRef.current) idFileInputRef.current.value = '';
    if (fullClient && fullClient.documents) {
      const idDoc = fullClient.documents.find(d => d.document_type === 'Identification');
      if (idDoc) setIdFileUrl(idDoc.file_path);
    }
    setActiveClientTab('personal');
    setShowModal(true);
    auditModalView(client ? 'Edit Client' : 'Add Client');
  };

  // Fetch client loans when opening details modal.
  // `inline` opens the client + loans workspace on the page instead of the popup.
  const openDetailsModal = async (client, inline = false) => {
    setClientLoadingText('Loading client profile...');
    setShowClientLoadingModal(true);
    let fullClient = client;
    try {
      const clientId = client?.personal_info?.id;
      if (clientId) {
        const token = localStorage.getItem('token');
        const profileRes = await axios.get(apiUrl(`/api/clients/${clientId}/profile`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        fullClient = {
          ...client,
          ...(profileRes.data || {}),
          user_account: client?.user_account ?? null,
          loanStatusMessage: client?.loanStatusMessage ?? null,
        };
      }
    } catch {
      // fall back
    } finally {
      setShowClientLoadingModal(false);
    }

    setDetailsClient(fullClient);
    setOpenDetailsAccordion('');
    setActiveClientTab('personal');
    setClientLoans([]);
    if (inline) {
      setWorkspaceOpen(true);
    } else {
      setShowDetailsModal(true);
    }
    auditModalView('Client Details');
    const openedId = fullClient?.personal_info?.id || client?.personal_info?.id;
    fetchClientLoans(openedId);
    fetchClientOnlyDocuments(openedId);
  };

  const fetchClientLoans = async (clientId) => {
    setLoadingLoans(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(apiUrl(`/api/clients/${clientId}/loans`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const loans = response.data || [];
      setClientLoans(loans);
      // The endpoint orders by created_at DESC, so the first row is the newest.
      fetchLatestLoanDocuments(loans[0] || null);
    } catch (err) {
      console.error('Error fetching client loans:', err);
      setClientLoans([]);
      setLatestLoan(null);
      setLatestLoanDocs([]);
    }
    setLoadingLoans(false);
  };

  // Documents with no loan attached. Taken from the dedicated endpoint rather
  // than the profile, whose list is one-per-type across the client and their
  // loans, so a client's own copy can be masked by a newer loan copy.
  const fetchClientOnlyDocuments = async (clientId) => {
    if (!clientId) {
      setClientOnlyDocs([]);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(apiUrl(`/api/document/client/${clientId}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const docs = Array.isArray(res?.data?.documents)
        ? res.data.documents
        : Array.isArray(res?.data)
        ? res.data
        : [];
      setClientOnlyDocs(docs);
    } catch {
      setClientOnlyDocs([]);
    }
  };

  // Documents attached to the client's most recent loan. Older loans' documents
  // stay where they are — on the loan itself, in its details panel.
  const fetchLatestLoanDocuments = async (loan) => {
    setLatestLoan(loan || null);
    if (!loan?.id) {
      setLatestLoanDocs([]);
      return;
    }
    setLoadingTopDocs(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(apiUrl(`/api/document/loan/${loan.id}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const docs = Array.isArray(res?.data?.documents)
        ? res.data.documents
        : Array.isArray(res?.data)
        ? res.data
        : [];
      setLatestLoanDocs(docs);
    } catch {
      setLatestLoanDocs([]);
    } finally {
      setLoadingTopDocs(false);
    }
  };

  // Opens the picked client into the side-by-side client + loans workspace.
  const openClientWorkspace = (client) => openDetailsModal(client, true);

  // Submitting the search box. A term is required — an empty search would just
  // list everybody, which is exactly what this page no longer does.
  const runSearch = (e) => {
    if (e) e.preventDefault();
    const term = searchInput.trim();
    if (!term) return;
    autoOpenedRef.current = null;
    setWorkspaceOpen(false);
    setDetailsClient(null);
    setHasSearched(true);
    setStatusFilter('all');
    if (term === searchTerm && currentPage === 1) {
      // Re-submitting the same term changes no state, so ask for the results
      // again explicitly instead of leaving the screen as it was.
      fetchClients(1, term, 'all');
    } else {
      setCurrentPage(1);
      setSearchTerm(term);
    }
  };

  // Back to the empty search area.
  const clearSearch = () => {
    setStatusFilter('all');
    autoOpenedRef.current = null;
    setWorkspaceOpen(false);
    setDetailsClient(null);
    setSearchInput('');
    setSearchTerm('');
    setHasSearched(false);
  };

  // Back to the list of matches (only meaningful when there was more than one).
  const backToResults = () => {
    setWorkspaceOpen(false);
    setDetailsClient(null);
  };

  // A search that lands on exactly one client opens it straight away.
  useEffect(() => {
    if (!hasSearched || workspaceOpen) return;
    if (clientsTotal !== 1 || clients.length !== 1) return;
    const only = clients[0];
    const onlyId = only?.personal_info?.id;
    if (!onlyId || autoOpenedRef.current === onlyId) return;
    autoOpenedRef.current = onlyId;
    openClientWorkspace(only);
  }, [clients, clientsTotal, hasSearched, workspaceOpen]);

  // Editing a client happens in a popup over the workspace. Once it closes,
  // reload the profile behind it so the panel is not left showing old values.
  const editModalWasOpenRef = useRef(false);
  useEffect(() => {
    const wasOpen = editModalWasOpenRef.current;
    editModalWasOpenRef.current = showModal;
    if (!wasOpen || showModal || !workspaceOpen) return;
    const id = detailsClient?.personal_info?.id;
    if (!id) return;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl(`/api/clients/${id}/profile`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDetailsClient(prev => (prev && prev.personal_info?.id === id)
          ? { ...prev, ...(res.data || {}) }
          : prev);
        fetchClientOnlyDocuments(id);
      } catch {
        // keep whatever is already on screen
      }
    })();
  }, [showModal, workspaceOpen, detailsClient?.personal_info?.id]);

  // Action button handlers
  const handleEditClient = (client) => {
    return openModal(client);
  };

  const handleAddLoanForClient = (client) => {
    return openLoanModal(client);
  };

  const handleResendActivationLink = async (client) => {
    try {
      const token = localStorage.getItem('token');
      const clientId = client?.personal_info?.id;
      if (!clientId) {
        setMessage('Failed to resend activation link: missing client reference.');
        return;
      }
      const res = await axios.post(
        apiUrl(`/api/clients/${clientId}/resend-activation`),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage(res.data?.message || 'Activation link sent successfully.');
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to resend activation link';
      setMessage(`Failed to resend activation link: ${msg}`);
    }
  };
  // ── Saved application (finish later) ───────────────────────────────────
  // A back-office user can park the loan they are capturing for a client and
  // come back to it; they are reminded by email every day until it is added
  // or discarded (the API's loanDraftReminders).
  const saveLoanForLater = async () => {
    const clientId = loanClient?.personal_info?.id;
    if (!clientId) return;
    setLoanDraftBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(apiUrl(`/api/loan-drafts/client/${clientId}`), {
        credit_assessment_id: addLoanMode === 'ai' ? (loanAssessment?.id || null) : null,
        term_months: loanTermMonths,
        principal_amount: loanForm.principal_amount,
        interest_rate: loanForm.interest_rate,
        duration_months: loanForm.duration_months,
        loan_reason_id: loanForm.loan_reason_id,
        application_type: loanForm.application_type,
        deduction_day: deductionDay,
        expense_codes: loanExpenseCodes,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setLoanDraft(res.data || null);
      closeAddLoanModal();
      setMessage('Loan application saved. Open Add Loan for this client to finish it — you will be reminded daily until it is added or discarded.');
    } catch (err) {
      setLoanMessage(err.response?.data?.error || 'The application could not be saved. Please try again.');
    } finally {
      setLoanDraftBusy(false);
    }
  };

  const discardLoanDraft = async () => {
    const clientId = loanClient?.personal_info?.id;
    setLoanDraftBusy(true);
    try {
      const token = localStorage.getItem('token');
      if (clientId) {
        await axios.delete(apiUrl(`/api/loan-drafts/client/${clientId}`), { headers: { Authorization: `Bearer ${token}` } });
      }
    } catch { /* already gone */ }
    setLoanDraft(null);
    setLoanDraftBusy(false);
  };

  // Opens the add-loan form filled in from the saved application.
  const resumeLoanDraft = async (draft = loanDraft) => {
    if (!draft) return;
    setLoanDraftBusy(true);
    try {
      let assessment = null;
      let term = null;
      if (draft.credit_assessment_id) {
        try {
          const token = localStorage.getItem('token');
          const res = await axios.get(apiUrl(`/api/prequalify/${draft.credit_assessment_id}`), {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.data?.status === 'Completed') {
            assessment = res.data;
            term = (res.data.qualification_matrix || []).find(r => Number(r.months) === Number(draft.term_months)) || null;
          }
        } catch { /* the score is gone: carry on with the answers only */ }
      }
      const str = (v) => (v === null || v === undefined ? undefined : String(v));
      setLoanForm(prev => ({
        ...prev,
        principal_amount: str(draft.principal_amount) ?? prev.principal_amount,
        interest_rate: str(draft.interest_rate) ?? prev.interest_rate,
        duration_months: str(term ? term.months : draft.duration_months) ?? prev.duration_months,
        loan_reason_id: str(draft.loan_reason_id) ?? prev.loan_reason_id,
        application_type: draft.application_type || prev.application_type,
      }));
      if (draft.deduction_day) setDeductionDay(String(draft.deduction_day));
      if (Array.isArray(draft.expense_codes)) setLoanExpenseCodes(draft.expense_codes);
      if (assessment) {
        setAddLoanMode('ai');
        setLoanAssessment(assessment);
        setLoanTermMonths(term ? Number(term.months) : null);
      } else {
        setAddLoanMode('manual');
        setLoanAssessment(null);
        setLoanTermMonths(null);
      }
      setLoanFormErrors({});
      setAddLoanPhase('form');
      setShowAddLoanModeChooser(false);
      setShowLoanModal(true);
    } finally {
      setLoanDraftBusy(false);
    }
  };

  // "Open full page" from a client quick look: /manage-clients?client=<clientId>
  // opens that client's workspace straight away.
  const openedClientFromLink = useRef(false);
  useEffect(() => {
    const clientId = parseInt(new URLSearchParams(window.location.search).get('client'), 10);
    if (!Number.isFinite(clientId) || openedClientFromLink.current) return;
    openedClientFromLink.current = true;
    openClientWorkspace({ personal_info: { id: clientId } });
  }, []);

  // From the reminder email or the dashboard: /manage-clients?resumeDraft=<clientId>
  // opens that client's Add Loan with the saved application.
  const resumeFromLink = useRef(false);
  useEffect(() => {
    const clientId = parseInt(new URLSearchParams(window.location.search).get('resumeDraft'), 10);
    if (!Number.isFinite(clientId) || resumeFromLink.current) return;
    resumeFromLink.current = true;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const [profileRes, draftRes] = await Promise.all([
          axios.get(apiUrl(`/api/clients/${clientId}/profile`), { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(apiUrl(`/api/loan-drafts/client/${clientId}`), { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        const client = profileRes.data;
        if (!client?.personal_info?.id || !draftRes.data) return;
        setLoanClient(client);
        setLoanDraft(draftRes.data);
        setCanAddLoan(true);
        await resumeLoanDraft(draftRes.data);
      } catch {
        setMessage('That saved loan application could not be opened — it may already have been added or discarded.');
      }
    })();
  }, []); // eslint-disable-line

  const closeAddLoanModal = () => {
    setShowLoanModal(false);
    setShowAddLoanModeChooser(false);
    setAddLoanMode('ai');
    setLoanMessage('');
    setAddLoanPhase('prequalify');
    setLoanAssessment(null);
    setLoanTermMonths(null);
  };

  // choice.termMonths is the repayment period picked on the result; the form
  // takes its duration and maximum amount from it.
  const handleLoanPrequalified = (result, _files, choice) => {
    if (!result) return;
    const months = Number(choice?.termMonths) || null;
    const maxForTerm = Number(choice?.term?.max_principal) || Number(result.affordability?.recommended_amount) || 0;
    setLoanAssessment(result);
    setLoanTermMonths(months);
    setLoanForm(prev => ({
      ...prev,
      duration_months: months ? String(months) : prev.duration_months,
      principal_amount: String(maxForTerm || prev.principal_amount || '')
    }));
    setDeductionDay(String(result.recommended_deduction_day || ''));
    setLoanFormErrors({});
    setAddLoanPhase('form');
  };

  // Back to the result with the period that was picked, to look again or
  // pick another.
  const handleBackToLoanScore = () => {
    setLoanFormErrors({});
    setAddLoanPhase('prequalify');
  };

  const handleRerunPrequalification = () => {
    setLoanTermMonths(null);
    setLoanAssessment(null);
    setLoanPreqKey(k => k + 1);
    setAddLoanPhase('prequalify');
  };

  // Handle loan addition
  const validateLoanForm = () => {
    let valid = true;
    const errors = {};
    if (!loanForm.loan_reason_id) {
      errors.loan_reason_id = 'Loan reason is required';
      valid = false;
    }
    if (!loanForm.application_type || !['Online', 'Walk-in'].includes(loanForm.application_type)) {
      errors.application_type = 'Application type is required';
      valid = false;
    }
    if (!loanForm.principal_amount || isNaN(loanForm.principal_amount)) {
      errors.principal_amount = 'Principal amount is required and must be a number';
      valid = false;
    }
    if (!loanForm.interest_rate || isNaN(loanForm.interest_rate)) {
      errors.interest_rate = 'Interest rate is required';
      valid = false;
    }
    if (!loanForm.duration_months || isNaN(loanForm.duration_months)) {
      errors.duration_months = 'Duration is required and must be a number';
      valid = false;
    }
    // The amount, term, rate and deduction day are checked by the API (the
    // loan quote, against what the client qualified for); its messages are
    // shown as they are.
    if (loanForm.quote_errors) {
      Object.entries(loanForm.quote_errors).forEach(([field, msg]) => {
        if (!errors[field]) errors[field] = msg;
      });
      valid = false;
    } else if (valid && !loanForm.monthly_payment) {
      errors.principal_amount = 'The loan amounts are still being worked out. Please try again in a moment.';
      valid = false;
    }
    if (!loanForm.start_date) {
      errors.start_date = 'Start date is required';
      valid = false;
    }
    if (!loanForm.due_date) {
      errors.due_date = 'Due date is required';
      valid = false;
    }
    // Documents are collected during pre-qualification; a completed assessment
    // satisfies the requirement. The file type and size are checked by the API,
    // whose message is shown if a file is refused.
    if (!bankStatementFile && (!aiEnabled || !loanAssessment)) {
      errors.bankStatementFile = 'Bank statement PDF is required';
      valid = false;
    }
    if (!payslipFile && (!aiEnabled || !loanAssessment)) {
      errors.payslipFile = 'Payslip PDF is required';
      valid = false;
    }
    // The credit report is mandatory on every application, staff capture too.
    const bureauError = !aiEnabled || !loanAssessment ? creditReportError({ file: bureauFile, reportDate: bureauDate, required: true }) : null;
    if (bureauError) {
      errors.bureauReport = bureauError;
      valid = false;
    }
    setLoanFormErrors(errors);
    return valid;
  };

  const handleAddLoan = async () => {
    if (!validateLoanForm()) return;
    // For Online applications: create loan now; client will review & sign
    if (loanForm.application_type === 'Online') {
      try {
        const token = localStorage.getItem('token');
        const form = new FormData();
        form.append('client_id', loanClient.personal_info.id);
        form.append('principal_amount', loanForm.principal_amount);
        form.append('interest_rate', loanForm.interest_rate);
        form.append('duration_months', loanForm.duration_months);
        // The API works out the amounts, and the dates from the deduction day.
        if (deductionDay) form.append('deduction_day', String(deductionDay));
        form.append('start_date', loanForm.start_date);
        form.append('due_date', loanForm.due_date);
        form.append('application_type', loanForm.application_type);
  if (loanForm.loan_reason_id) form.append('loan_reason_id', loanForm.loan_reason_id);
        if (aiEnabled && loanAssessment?.id) form.append('credit_assessment_id', String(loanAssessment.id));
        if (bankStatementFile) form.append('bankStatement', bankStatementFile);
        if (payslipFile) form.append('payslip', payslipFile);
        if (bureauFile) {
          form.append('bureauReport', bureauFile);
          form.append('bureau_report_date', bureauDate);
        }
        // The chosen expenses; the server prices them from the configured types.
        form.append('additional_expenses', JSON.stringify(loanExpenseCodes));
        await axios.post(apiUrl('/api/loans/add-loans'), form, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        setShowLoanModal(false);
        setLoanForm({});
        setLoanFormErrors({});
        setLoanClient(null);
        setBankStatementFile(null);
        setPayslipFile(null); setBureauFile(null); setBureauDate('');
        setDeductionDay('');
        setAddLoanPhase('prequalify');
        setLoanAssessment(null);
        setLoanTermMonths(null);
        setMessage('Loan created. Client must review and sign the contract online.');
        setLoansRefreshKey(k => k + 1);
        fetchClients();
      } catch (err) {
        setLoanMessage(err.response?.data?.error || err.response?.data?.message || 'Failed to create loan');
      }
      return;
    }
    // Walk-in applications: proceed to in-person contract review/signing
    setShowContractModal(true);
    setShowLoanModal(false);
  };

  // Modified function to handle contract generation with signature from ContractPreview
  const handleGenerateContract = async (clientDataWithSignature) => {
    // New walk-in flow: create loan, then submit client signatures; PDF will be generated on approval and saved to DB only
    if (!clientDataWithSignature.signature) {
      setLoanMessage('Signature is required');
      return;
    }
    setIsGeneratingContract(true);
    try {
      // Cache loan ID on first successful creation to avoid duplicates on retry
      if (!window.__walkInPendingLoanId) {
        const loanResult = await submitLoanWithContract(
          { ...loanForm, application_type: 'Walk-in', additional_expense_codes: loanExpenseCodes, deduction_day: deductionDay },
          loanClient.personal_info.id,
          undefined,
          bankStatementFile,
          payslipFile,
          undefined,
          (aiEnabled && loanAssessment?.id) || undefined,
          { bureauReport: bureauFile ? { file: bureauFile, date: bureauDate } : null }
        );
        if (!loanResult.success || !loanResult.data?.loan?.id) {
          setLoanMessage(loanResult.message || 'Failed to submit loan');
          setIsGeneratingContract(false);
          return;
        }
        window.__walkInPendingLoanId = loanResult.data.loan.id;
      }
      const loanId = window.__walkInPendingLoanId;

      const token = localStorage.getItem('token');
      await axios.post(
        apiUrl(`/api/loans/${loanId}/client-sign`),
        {
          references: {
            reference1_name: clientDataWithSignature.reference1_name,
            reference1_tel: clientDataWithSignature.reference1_tel,
            reference2_name: clientDataWithSignature.reference2_name,
            reference2_tel: clientDataWithSignature.reference2_tel,
          },
          signatureLocations: clientDataWithSignature.signatureLocations,
          // How the money is paid out once approved (mandatory).
          payout: clientDataWithSignature.payout,
          signature: clientDataWithSignature.signature,
          initials: clientDataWithSignature.initials,
          witness1Signature: clientDataWithSignature.witness1Signature,
          witness1Initials: clientDataWithSignature.initials?.witness1Initials,
          lenderSignature: clientDataWithSignature.lenderSignature,
          witness2Signature: clientDataWithSignature.witness2Signature
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setShowContractModal(false);
      setLoanForm({});
      setLoanFormErrors({});
      setLoanClient(null);
      setBankStatementFile(null);
      setPayslipFile(null); setBureauFile(null); setBureauDate('');
      setDeductionDay('');
      setContractUrl('');
      setAddLoanPhase('prequalify');
      setLoanAssessment(null);
      setLoanTermMonths(null);
  setMessage('Loan created and client signatures saved. Status set to Awaiting Approval. Contract will be generated upon approval.');
      setLoansRefreshKey(k => k + 1);
      fetchClients();
      // Clear cached ID after success
      window.__walkInPendingLoanId = null;
    } catch (err) {
      setLoanMessage(err.response?.data?.error || 'Failed to submit signatures');
    }
    setIsGeneratingContract(false);
  };

  // Function to go back to loan form
  const handleBackToLoanForm = () => {
    setShowContractModal(false);
    setShowLoanModal(true);
  };

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const getMessageClass = () => {
    if (message.includes('Failed') || message.includes('duplicate') || message.includes('already exists')) {
      return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300';
    } else if (message.includes('successfully') || message.includes('added')) {
      return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
    }
    return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300';
  };

  const getModalMessageClass = () => {
    if (modalMessage.includes('Failed') || modalMessage.includes('duplicate') || modalMessage.includes('already exists')) {
      return 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300';
    } else if (modalMessage.includes('successfully') || modalMessage.includes('added')) {
      return 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300';
    }
    return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300';
  };

  const totalPages = Math.max(1, Math.ceil((clientsTotal || 0) / PAGE_SIZE));
  const paginatedClients = clients;

  // Which of the three screens the page is on: the search area, the list of
  // matches, or one client open beside their loans.
  const showWorkspace = !!(workspaceOpen && detailsClient);
  const showResults = !showWorkspace && hasSearched;
  const showLanding = !showWorkspace && !hasSearched;
  // Prefer the freshly-listed summary for the header so status changes show up.
  const workspaceClient = showWorkspace
    ? (clients.find(c => c?.personal_info?.id === detailsClient?.personal_info?.id) || detailsClient)
    : null;

  // Non-destructive completeness checks (mirror the section validators without
  // setting error state) so the stepper only shows green when a section's
  // mandatory fields are actually filled.
  const _filled = (v) => v !== undefined && v !== null && String(v).trim() !== '';
  const personalSectionComplete =
    ['email', 'first_name', 'last_name', 'id_passport_no', 'postal_address', 'marital_status', 'gender'].every(f => _filled(formData[f]))
    && _filled(formData.phone_number) && validatePhone9(formData.phone_number)
    && (selectedClient ? true : (!!idFile && !!porFile));
  const occupationSectionComplete =
    ['occupation', 'employer_name', 'employer_address', 'payslip_number'].every(f => _filled(formData[f]))
    && _filled(formData.salary) && !isNaN(Number(formData.salary))
    && _filled(formData.employer_phone) && validatePhone9(formData.employer_phone);
  const bankSectionComplete =
    ['bank_name', 'branch_code', 'account_number', 'account_type'].every(f => _filled(formData[f]));

  const stepperSteps = [
    { key: 'personal', label: 'Personal Info', isComplete: personalSectionComplete },
    { key: 'occupation', label: 'Occupation Info', isComplete: occupationSectionComplete },
    { key: 'bank', label: 'Bank Info', isComplete: bankSectionComplete },
  ];

  // Re-fetch a client's profile (incl. documents) after a staff document
  // replacement so the details view shows the new file immediately.
  const reloadDetailsClientDocuments = async (clientObj) => {
    const clientId = clientObj?.personal_info?.id || detailsClient?.personal_info?.id;
    if (!clientId) return;
    try {
      const token = localStorage.getItem('token');
      const profileRes = await axios.get(apiUrl(`/api/clients/${clientId}/profile`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDetailsClient(prev => ({ ...(prev || {}), ...(profileRes.data || {}) }));
      setMessage('Document updated successfully.');
    } catch {
      setMessage('Document saved, but the preview could not refresh. Reopen the client to see the change.');
    }
  };

  // The read-only field set shown for a client. Shared by the Client Details
  // popup and the client + loans workspace so both stay identical.
  const detailsFormData = (c) => ({
    // Personal info
    email: c?.personal_info?.email || '',
    first_name: c?.personal_info?.first_name || '',
    middle_name: c?.personal_info?.middle_name || '',
    last_name: c?.personal_info?.last_name || '',
    gender: c?.personal_info?.gender || '',
    id_passport_no: c?.personal_info?.id_passport_no || '',
    postal_address: c?.personal_info?.postal_address || '',
    erf_no: c?.personal_info?.erf_no || '',
    street_name: c?.personal_info?.street_name || '',
    suburb: c?.personal_info?.suburb || '',
    city: c?.personal_info?.city || '',
    country: c?.personal_info?.country || '',
    residential_address: c?.personal_info?.residential_address || '',
    phone_number: c?.personal_info?.phone_number || '',
    marital_status: c?.personal_info?.marital_status || '',

    // Occupation info
    occupation: c?.occupation_info?.occupation || '',
    salary: c?.occupation_info?.salary ?? '',
    employer_name: c?.occupation_info?.employer_name || '',
    employer_phone: c?.occupation_info?.employer_phone || '',
    employer_address: c?.occupation_info?.employer_address || '',
    payslip_number: c?.occupation_info?.payslip_number || '',

    // Bank info
    bank_name: c?.bank_info?.bank_name || '',
    branch_code: c?.bank_info?.branch_code || '',
    account_number: c?.bank_info?.account_number || '',
    account_type: c?.bank_info?.account_type || '',
  });

  // View opens the document in a frame under the cards rather than a new tab.
  // A blocked pop-up used to send the whole app to the PDF and lose the page.
  // Render accordion sections using the new components
  const renderAccordionSections = (readOnly = false, detailsData = null, hideDocuments = false) => {
    const data = detailsData || formData;
    const client = detailsData ? detailsClient : selectedClient;
    // Staff may replace any onboarding document while the client is unapproved.
    const allowDocReplace = canManageClients && !!client?.personal_info && client.personal_info.is_active === false;
    const idDoc = detailsData
      ? (client.documents && client.documents.find(d => d.document_type === 'Identification'))
      : (selectedClient && selectedClient.documents && selectedClient.documents.find(d => d.document_type === 'Identification'));
    const idFileUrlToShow = detailsData ? idDoc?.file_path : idFileUrl;
    const idDocId = idDoc?.id;
    const idDocName = idDoc?.file_path;
    const porDoc = detailsData
      ? (client.documents && client.documents.find(d => d.document_type === 'Proof Of Residence'))
      : (selectedClient && selectedClient.documents && selectedClient.documents.find(d => d.document_type === 'Proof Of Residence'));
    const porDocId = porDoc?.id;
    const porDocName = porDoc?.file_path;

    const tabs = [
      { key: 'personal', label: 'Personal Information' },
      { key: 'occupation', label: 'Occupation Information' },
      { key: 'bank', label: 'Bank Information' },
    ];
    const active = tabs.some(t => t.key === activeClientTab) ? activeClientTab : 'personal';

    const saveBar = (sectionKey, onSave, label) => (selectedClient && !readOnly && onSave) ? (
      <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-100 dark:border-slate-700/60">
        <ActionButton type="button" onClick={onSave} disabled={loadingSection === sectionKey}
          className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-60">
          {loadingSection === sectionKey ? 'Saving…' : label}
        </ActionButton>
        {sectionMessages[sectionKey] && (
          <span className={`text-sm ${String(sectionMessages[sectionKey]).includes('success') ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {sectionMessages[sectionKey]}
          </span>
        )}
      </div>
    ) : null;

    // Section completeness from the data being shown (form state when editing,
    // client record when viewing details) — green only when mandatory fields are filled.
    const _f = (v) => v !== undefined && v !== null && String(v).trim() !== '';
    const sectionComplete = {
      personal: ['email', 'first_name', 'last_name', 'id_passport_no', 'phone_number', 'postal_address', 'marital_status', 'gender'].every(f => _f(data[f])),
      occupation: ['occupation', 'employer_name', 'employer_phone', 'employer_address', 'payslip_number', 'salary'].every(f => _f(data[f])),
      bank: ['bank_name', 'branch_code', 'account_number', 'account_type'].every(f => _f(data[f])),
    };

    return (
      <div>
        {/* Section stepper — same segmented style as the Add Client stepper */}
        <div className="flex items-center gap-1.5 sm:gap-2 mb-6 w-full">
          {tabs.map((t, idx) => {
            const isActive = active === t.key;
            const isComplete = sectionComplete[t.key] === true;
            return (
              <React.Fragment key={t.key}>
                {idx > 0 && <ChevronRight size={16} className="flex-shrink-0 text-gray-300 dark:text-slate-600" />}
                <button
                  type="button"
                  onClick={() => setActiveClientTab(t.key)}
                  className={`flex-1 text-center px-2 sm:px-3 py-2.5 rounded-lg text-xs sm:text-sm font-semibold whitespace-nowrap transition-colors ${
                    isActive
                      ? 'bg-blue-600 text-white shadow-sm'
                      : isComplete
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800/40'
                      : 'bg-gray-100 text-gray-500 border border-gray-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600'
                  }`}
                >
                  {!isActive && isComplete && <CheckCircle2 size={13} className="inline mr-1 -mt-0.5" />}
                  {t.label}
                </button>
              </React.Fragment>
            );
          })}
        </div>

        {active === 'personal' && (
          <div>
            <PersonalInfoForm
              formData={data}
              setFormData={!readOnly ? updateFormData : () => {}}
              formErrors={formErrors}
              idFile={idFile}
              setIdFile={!readOnly ? setIdFile : () => {}}
              idFileUrl={idFileUrlToShow}
              idDocId={idDocId}
              idDocName={idDocName}
              porFile={!readOnly ? porFile : undefined}
              setPorFile={!readOnly ? setPorFile : undefined}
              porDocId={porDocId}
              porDocName={porDocName}
              hideHeader={true}
              hideDocuments={hideDocuments}
              readOnly={readOnly}
              isNewClient={!selectedClient}
              documents={(client && client.documents) || []}
              allowDocReplace={allowDocReplace}
              replaceClientId={client?.personal_info?.id}
              onDocReplaced={() => reloadDetailsClientDocuments(client)}
            />
            {saveBar('personal', handleUpdatePersonal, 'Save Personal Info')}
          </div>
        )}

        {active === 'occupation' && (
          <div>
            <OccupationInfoForm
              formData={data}
              setFormData={!readOnly ? updateFormData : () => {}}
              formErrors={formErrors}
              hideHeader={true}
              readOnly={readOnly}
            />
            {saveBar('occupation', handleUpdateOccupation, 'Save Occupation Info')}
          </div>
        )}

        {active === 'bank' && (
          <div>
            <BankInfoForm
              formData={data}
              setFormData={!readOnly ? updateFormData : () => {}}
              formErrors={formErrors}
              hideHeader={true}
              readOnly={readOnly}
            />
            {saveBar('bank', handleUpdateBank, 'Save Bank Info')}
          </div>
        )}
      </div>
    );
  };

  // Render stepper form for adding new clients
  const renderStepperForm = () => (
    <form
      className="flex flex-col gap-6 text-sm form-compact"
      noValidate
      onSubmit={e => {
        e.preventDefault();
        const run = async () => {
          if (addStep === 0) {
            setAddClientMessage('');
            setDocumentSubmissionGuard(null);
            setAddStep(1);
            return;
          }
          if (addStep === 1) {
            setAddClientMessage('');
            setDocumentSubmissionGuard(null);
            setAddStep(2);
            return;
          }
          if (addStep === 2) handleAddClient();
        };
        run();
      }}
    >
      {aiEnabled && addClientMode === 'ai' && onboardingAutoFillPanel}

      <Stepper steps={stepperSteps} currentStep={addStep} setCurrentStep={step => {
        setAddClientMessage('');
        setDocumentSubmissionGuard(null);
        setAddStep(step);
      }} />

      {addClientMessage && (
        <div className="p-3 rounded bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800/50 text-sm">
          {addClientMessage}
        </div>
      )}

      {documentSubmissionGuardPanel}
      
      {addStep === 0 && (
        <>
          <PersonalInfoForm
            formData={formData}
            setFormData={updateFormData}
            formErrors={formErrors}
            idFile={idFile}
            setIdFile={setIdFile}
            porFile={porFile}
            setPorFile={setPorFile}
            isNewClient={true}
            showDocumentUploads={false}
            onFieldFocus={handleTrainingFieldFocus}
          />
        </>
      )}

      {addStep === 1 && (
        <OccupationInfoForm
          formData={formData}
          setFormData={updateFormData}
          formErrors={formErrors}
          onFieldFocus={handleTrainingFieldFocus}
        />
      )}

      {addStep === 2 && (
        <BankInfoForm
          formData={formData}
          setFormData={updateFormData}
          formErrors={formErrors}
          onFieldFocus={handleTrainingFieldFocus}
        />
      )}

      <div className="flex justify-end gap-3 pt-5 border-t border-gray-100 dark:border-slate-700 mt-2">
        {addStep > 0 && (
          <button
            type="button"
            onClick={() => {
              setAddClientMessage('');
              setDocumentSubmissionGuard(null);
              setAddStep(addStep - 1);
            }}
            className="px-5 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition text-sm font-medium"
          >
            Back
          </button>
        )}
        {addStep < 2 ? (
          <button
            type="submit"
            className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-semibold shadow-sm"
          >
            Next
          </button>
        ) : (
          <>
            {documentGuardHasOverride && (
              <ActionButton
                type="button"
                onClick={() => handleAddClient({ forceSubmit: true })}
                disabled={!!clientLimitStatus?.reached || isAddingClient}
                className={`px-5 py-2 rounded-lg transition text-sm font-semibold shadow-sm ${(clientLimitStatus?.reached || isAddingClient) ? 'bg-gray-200 text-gray-500 cursor-not-allowed dark:bg-slate-700 dark:text-slate-500' : 'bg-amber-600 text-white hover:bg-amber-700'}`}
                title="Force Submit"
              >
                {isAddingClient ? 'Submitting...' : 'Force Submit'}
              </ActionButton>
            )}
            <ActionButton
              type="submit"
              onClick={(e) => {
                e.preventDefault();
                return handleAddClient();
              }}
              disabled={!!clientLimitStatus?.reached || isAddingClient}
              className={`px-5 py-2 rounded-lg transition text-sm font-semibold shadow-sm ${(clientLimitStatus?.reached || isAddingClient) ? 'bg-gray-200 text-gray-500 cursor-not-allowed dark:bg-slate-700 dark:text-slate-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
              title={clientLimitStatus?.reached ? 'Reached client limit' : 'Add Client'}
            >
              {clientLimitStatus?.reached ? 'Reached client limit' : isAddingClient ? 'Adding...' : 'Add Client'}
            </ActionButton>
          </>
        )}
        <button
          type="button"
          onClick={() => setShowModal(false)}
          className="px-5 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition text-sm font-medium"
        >
          Cancel
        </button>
      </div>
    </form>
  );

  // Function to open loan modal
  const openLoanModal = async (client) => {
    // Ensure no stale pending loan ID is carried over from a previous attempt
    window.__walkInPendingLoanId = null;
    // Whether a loan can be added for the client is the API's call (the same
    // rule add-loans applies); its message is shown when it cannot.
    try {
      const token = localStorage.getItem('token');
      const checkRes = await axios.get(
        apiUrl(`/api/clients/${client.personal_info.id}/can-add-loan`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (checkRes.data?.canAdd) {
        // Can add loan
        setLoanClient(client);
        setLoanForm({});
        setLoanFormErrors({});
        setLoanMessage('');
        setLoanStatusMessage('');
        setCanAddLoan(true);
        setLoanBannerMessage('');
        setDeductionDay('');
        setBankStatementFile(null);
        setPayslipFile(null); setBureauFile(null); setBureauDate('');
        setAddLoanPhase('prequalify');
        setLoanAssessment(null);
        setLoanTermMonths(null);
        setLoanPreqKey(k => k + 1);
        setLoanExpenseCodes(DEFAULT_EXPENSE_CODES);
        setLoanDraft(null);
        let savedDraft = null;
        try {
          const draftRes = await axios.get(apiUrl(`/api/loan-drafts/client/${client.personal_info.id}`), {
            headers: { Authorization: `Bearer ${token}` }
          });
          savedDraft = draftRes.data || null;
        } catch { /* none saved */ }
        setLoanDraft(savedDraft);
        if (bankStatementInputRef.current) bankStatementInputRef.current.value = '';
        if (payslipInputRef.current) payslipInputRef.current.value = '';
        // The eligibility check has passed, so now ask how to capture it.
        // Without AI there is only one way, so skip straight to the form.
        if (aiEnabled && savedDraft) {
          // A saved application: continue it, or start a new one.
          setShowAddLoanModeChooser(true);
        } else if (aiEnabled) {
          // Every new loan starts with the AI pre-qualification.
          setAddLoanMode('ai');
          setAddLoanPhase('prequalify');
          setLoanAssessment(null);
          setLoanTermMonths(null);
          setLoanPreqKey(k => k + 1);
          setShowLoanModal(true);
        } else {
          setAddLoanMode('manual');
          setAddLoanPhase('form');
          setShowLoanModal(true);
        }
      } else {
        setMessage(checkRes.data?.message || 'A new loan cannot be added for this client yet.');
      }
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to check loan status.');
    }
  };

  // The recognisable fields a search result card and the workspace header show.
  const buildClientRow = (client) => ({
    id: client.personal_info.id,
    client_code: client.personal_info.client_code || client.personal_info.clientCode || '-',
    fullName: [
      client.personal_info.first_name,
      client.personal_info.middle_name,
      client.personal_info.last_name
    ].filter(name => name).join(' '),
    id_passport_no: client.personal_info.id_passport_no,
    phone_number: client.personal_info.phone_number,
    gender: client.personal_info.gender,
    marital_status: client.personal_info.marital_status,
    is_active: client.personal_info.is_active,
    awaiting_update: client.personal_info.awaiting_update,
    update_resubmitted_at: client.personal_info.update_resubmitted_at,
    // Store the entire client object for actions
    clientData: client
  });

  // A client's state, the same in the list and the workspace: active, sent
  // back for an update, resubmitted, or inactive. yesNo: the list's "Active"
  // column says Yes / No for the first and last.
  const clientStatusBadge = (row, { yesNo = false } = {}) => {
    if (row?.is_active) return <StatusBadge status="Active" label={yesNo ? 'Yes' : 'Active'} />;
    if (row?.awaiting_update) return <StatusBadge status="Awaiting update" title="Returned to the client: awaiting their update" />;
    if (row?.update_resubmitted_at) return <StatusBadge status="Resubmitted" title="Client resubmitted: ready for review/approval" />;
    return <StatusBadge status="Inactive" label={yesNo ? 'No' : 'Inactive'} />;
  };

  // Define table columns and actions for the ResponsiveTable
  const tableColumns = [
    {
      field: 'client_code',
      header: 'Client Code',
      formatter: (value) => <span className="font-mono text-gray-800 dark:text-slate-100">{value}</span>
    },
    {
      field: 'fullName',
      header: 'Full Name',
      formatter: (value) => <span className="font-medium">{value}</span>
    },
    {
      field: 'id_passport_no',
      header: 'ID/Passport No'
    },
    {
      field: 'phone_number',
      header: 'Phone'
    },
    {
      field: 'gender',
      header: 'Gender'
    },
    {
      field: 'marital_status',
      header: 'Marital Status'
    },
    {
      field: 'is_active',
      header: 'Active',
      formatter: (value, row) => clientStatusBadge(row, { yesNo: true })
    },
    {
      field: 'actions',
      header: 'Actions',
      formatter: (_, row) => renderActions(row)
    }
  ];

  // Format data for the ResponsiveTable
  const tableData = paginatedClients.map(buildClientRow);

  // Custom action column rendering: direct inline buttons (no dropdown)
  const renderActions = (row, { hideViewDetails = false } = {}) => {
    // Only offer the resend link while the account is still inactive (either a
    // self-registered client awaiting admin approval, or a staff-onboarded
    // client who hasn't completed their first-login activation yet).
    const canResendActivationLink =
      canManageClients &&
      row.clientData?.user_account &&
      !row.clientData.user_account.is_active;

    return (
      <div className="flex flex-wrap items-center gap-2">
        {!hideViewDetails && (
          <ActionButton
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              return openClientWorkspace(row.clientData);
            }}
            className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300"
          >
            👁️ View Details
          </ActionButton>
        )}
        {canManageClients && (
          <ActionButton
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              return handleEditClient(row.clientData);
            }}
            className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-amber-50 text-amber-600 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300"
          >
            ✏️ Edit Client
          </ActionButton>
        )}
        {row.is_active && canManageLoans && (
          <ActionButton
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              return handleAddLoanForClient(row.clientData);
            }}
            className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
              row.clientData.loanStatusMessage
                ? 'bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300'
                : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300'
            }`}
          >
            💳 Add Loan
          </ActionButton>
        )}
        {canResendActivationLink && (
          <ActionButton
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              return handleResendActivationLink(row.clientData);
            }}
            className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-purple-50 text-purple-600 hover:bg-purple-100 dark:bg-purple-900/30 dark:text-purple-300"
            busyLabel="📨 Sending…"
          >
            📨 Resend Activation Link
          </ActionButton>
        )}
        {row.is_active ? (
          canManageClients && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                requestClientAction(row.id, 'deactivate', row.fullName);
              }}
              className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300"
            >
              🚫 Deactivate
            </button>
          )
        ) : (
          (canManageClients || canApproveClient) && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  requestClientAction(row.id, 'activate', row.fullName);
                }}
                className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300"
              >
                ✅ Approve Client
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  openReturnForUpdate(row.id, row.fullName);
                }}
                className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-amber-50 text-amber-600 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300"
              >
                ✉️ Return for update
              </button>
            </>
          )
        )}
      </div>
    );
  };

  return (
    <div className="max-w-full min-h-screen">
      {showClientLoadingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg p-6 w-72 text-center space-y-3">
            <div className="flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" aria-label="Loading spinner" />
            </div>
            <div className="text-sm font-semibold text-gray-800 dark:text-slate-100">{clientLoadingText}</div>
            <div className="text-xs text-gray-500 dark:text-slate-400">Please wait...</div>
          </div>
        </div>
      )}
      {/* KPI Row — the whole book at a glance, on the search screen only */}
      {showLanding && (
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-3 mb-2">
        {(() => {
          const m = clientsDbMetrics
            ? {
              total: Number(clientsDbMetrics.total || 0),
              active: Number(clientsDbMetrics.active || 0),
              inactive: Number(clientsDbMetrics.inactive || 0)
            }
            : clientsMetrics(clients);
          // The API's count: clients with a loan that is not Paid Off,
          // Rejected, Cancelled or Terminated. Not redone here; a dash when
          // the API's metrics did not load.
          const withLoans = clientsDbMetrics && clientsDbMetrics.withLoans != null
            ? Number(clientsDbMetrics.withLoans)
            : '—';
          return (
          <>
            <KpiCard label="Total Clients" value={m.total} color="gray" />
            <KpiCard label="Active Clients" value={m.active} color="green" />
            <KpiCard label="Inactive Clients" value={m.inactive} color={m.inactive>0?'amber':'green'} />
            <KpiCard label="Clients with an open loan" value={withLoans} color="blue"
              title="Clients with a loan that is not Paid Off, Rejected, Cancelled or Terminated" />
          </>
        );
        })()}
      </div>
      )}

      {showLanding && clientLimitStatus?.limitEnabled && (
        <div
          className={(() => {
            const remaining = Number(clientLimitStatus.remaining);
            if (clientLimitStatus.reached || remaining <= 0) return 'mb-2 p-2 rounded-lg border border-red-300 dark:border-red-700/50 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300';
            if (remaining <= 2) return 'mb-2 p-2 rounded-lg border border-red-200 dark:border-red-700/50 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300';
            if (remaining <= 5) return 'mb-2 p-2 rounded-lg border border-orange-300 dark:border-orange-700/50 bg-orange-100 dark:bg-orange-900/30 text-orange-950 dark:text-orange-300';
            if (remaining <= 10) return 'mb-2 p-2 rounded-lg border border-orange-200 dark:border-orange-700/40 bg-orange-50 dark:bg-orange-900/20 text-orange-900 dark:text-orange-300';
            return 'mb-2 p-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 text-gray-800 dark:text-slate-200';
          })()}
          role="alert"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
            <span className="font-semibold">Client Limit</span>
            <span className="text-sm">
              {clientLimitStatus.clientCount}/{clientLimitStatus.limit} used
              {' · '}
              {clientLimitStatus.remaining} remaining
            </span>
          </div>
        </div>
      )}
      {error && <div className="p-2 rounded-lg mb-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">{error}</div>}
      {message && (
        <div className={`p-2 rounded-lg mb-2 flex items-center justify-between ${getMessageClass()}`}>
          <span>{message}</span>
          <button
            onClick={() => setMessage('')}
            className="ml-4 px-2 py-1 rounded-full text-lg font-bold hover:bg-gray-200 focus:outline-none"
            aria-label="Close"
            title="Close"
          >
            ×
          </button>
        </div>
      )}
      {confirmAction && (
        <div className="flex items-center justify-between bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 p-2 rounded mb-4 shadow border border-yellow-200 dark:border-yellow-700/50">
          <span>
            Are you sure you want to <strong>{confirmAction.action}</strong> client <strong>{confirmAction.name}</strong>?
          </span>
          <div className="flex gap-2">
            {confirmAction.action === 'deactivate' ? (
              <Button
                onClick={() => handleDeactivateClient(confirmAction.clientId)}
                variant="danger"
                size="sm"
                busyLabel="Working…"
              >
                Confirm
              </Button>
            ) : (
              <Button
                onClick={() => handleActivateClient(confirmAction.clientId)}
                variant="success"
                size="sm"
                busyLabel="Working…"
              >
                Confirm
              </Button>
            )}
            <Button
              onClick={() => setConfirmAction(null)}
              variant="secondary"
              size="sm"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      {returnModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !returningClient && setReturnModal(null)}>
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-slate-100 mb-1">Return registration for update</h3>
            <p className="text-sm text-gray-600 dark:text-slate-400 mb-3">
              <strong>{returnModal.name}</strong> will be emailed a secure link to reopen and resubmit their registration. The account stays unapproved until they resubmit and you approve it.
            </p>
            <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1">Note to the client — what needs to be updated? *</label>
            <textarea
              value={returnNote}
              onChange={(e) => setReturnNote(e.target.value)}
              rows={4}
              autoFocus
              placeholder="e.g. Your payslip is unclear — please re-upload a readable copy, and correct your ID number."
              className="w-full p-2"
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button onClick={() => setReturnModal(null)} variant="secondary" size="sm" disabled={returningClient}>Cancel</Button>
              <Button onClick={handleReturnForUpdate} variant="primary" size="sm" disabled={returningClient || !returnNote.trim()}>
                {returningClient ? 'Sending…' : 'Send update link'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {!(showModal || showDetailsModal || showLoanModal || showContractModal) && (
      <>
      {/* ── 1. Search area: no clients are listed until one is searched for ── */}
      {showLanding && (
        <div className="w-full">
          <div className="w-full border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500" />
            <div className="px-4 sm:px-8 py-8 sm:py-10 text-center">
              <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center shadow-lg shadow-blue-500/25">
                <Search size={26} />
              </div>
              <h2 className="mt-5 text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-50">
                Find a client
              </h2>
              <p className="mt-2 text-sm text-gray-500 dark:text-slate-400">
                Search by name, ID or passport number, client code, loan code, email address
                or cellphone number. A single match opens the client and their loans side by side.
              </p>

              {/* The field and the button share the toolbar height, so they line
                  up with each other and with the controls on every other page;
                  the icons sit centred on the field itself. */}
              <form onSubmit={runSearch} className="mt-6 flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="relative flex-1 min-w-0">
                  <Search size={15} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500" />
                  <input
                    type="text"
                    autoFocus
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    placeholder="Name, ID / passport, client code, loan code, email or cellphone — * for wildcard"
                    aria-label="Search clients"
                    className="toolbar-control w-full !pl-8 !pr-8"
                  />
                  {searchInput && (
                    <button
                      type="button"
                      onClick={() => setSearchInput('')}
                      aria-label="Clear search"
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                <Button
                  type="submit"
                  disabled={!searchInput.trim()}
                  variant="primary"
                  size="sm"
                  className="toolbar-control flex-shrink-0"
                  icon={<Search size={14} />}
                >
                  Search
                </Button>
              </form>

              <div className="mt-5 flex flex-wrap items-center justify-center gap-2 text-[11px] font-medium">
                {['First, middle or last name', 'ID / Passport No', 'Client code', 'Loan code', 'Email address', 'Cellphone number'].map(hint => (
                  <span
                    key={hint}
                    className="px-2.5 py-1 rounded-full bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400 border border-gray-200 dark:border-slate-700"
                  >
                    {hint}
                  </span>
                ))}
              </div>

              {/* Search tip — a callout rather than grey small print, so it is
                  seen before the first search. The examples fill the box. */}
              <div className="mt-4 rounded-lg border border-blue-200 dark:border-blue-800/50 bg-blue-50 dark:bg-blue-900/20 px-4 py-3 text-left">
                <p className="text-sm font-semibold text-blue-900 dark:text-blue-200 flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-blue-600 text-white font-mono text-base leading-none">*</span>
                  Search tip: use <code className="px-1.5 py-0.5 rounded bg-white dark:bg-slate-800 border border-blue-200 dark:border-blue-700 font-mono text-blue-700 dark:text-blue-300">*</code> as a wildcard
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-blue-900 dark:text-blue-200">
                  <span className="text-xs text-blue-800 dark:text-blue-300">Try:</span>
                  {[
                    { q: 'Jo*', hint: 'starts with Jo' },
                    { q: '*doe', hint: 'ends with doe' },
                    { q: '081*', hint: 'numbers starting 081' },
                  ].map(({ q, hint }) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => setSearchInput(q)}
                      title={`Search ${hint}`}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white dark:bg-slate-800 border border-blue-300 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      <span className="font-mono font-semibold text-blue-700 dark:text-blue-300">{q}</span>
                      <span className="text-xs text-gray-600 dark:text-slate-400">{hint}</span>
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-blue-800 dark:text-blue-300">
                  Without a <span className="font-mono font-semibold">*</span>, a search matches anywhere in the field.
                </p>
              </div>

              {canManageClients && (
                <div className="mt-8 pt-6 border-t border-gray-100 dark:border-slate-800">
                  <p className="text-xs text-gray-400 dark:text-slate-500 mb-3">Not on the system yet?</p>
                  <Button
                    onClick={() => {
                      if (!aiEnabled) {
                        setAddClientMode('manual');
                        openModal();
                        return;
                      }
                      setShowAddModeChooser(true);
                    }}
                    disabled={!!clientLimitStatus?.reached}
                    variant="primary"
                    size="sm"
                    title={clientLimitStatus?.reached ? 'Reached client limit' : 'Add Client'}
                    icon={
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path d="M12 4v16m8-8H4" />
                      </svg>
                    }
                  >
                    {clientLimitStatus?.reached ? 'Reached client limit' : 'Add Client'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Compact search bar — stays on screen once a search has been made */}
      {!showLanding && (
        <form onSubmit={runSearch} className="mb-3 flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1 min-w-0">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500" />
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Name, ID / passport, client code, loan code, email or cellphone — * for wildcard"
              aria-label="Search clients"
              className="toolbar-control w-full !pl-9 !pr-9"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput('')}
                aria-label="Clear search"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={!searchInput.trim()}
              className="toolbar-control inline-flex items-center gap-1.5 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors"
            >
              <Search size={15} /> Search
            </button>
            {showWorkspace && clientsTotal > 1 ? (
              <button
                type="button"
                onClick={backToResults}
                className="toolbar-control inline-flex items-center gap-1.5 px-4 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold shadow-sm transition-colors whitespace-nowrap"
              >
                <ArrowLeft size={15} /> Back to {clientsTotal} matches
              </button>
            ) : (
              <Button onClick={clearSearch} variant="secondary" size="sm" className="toolbar-control">Start over</Button>
            )}
          </div>
        </form>
      )}

      {/* ── 2. The matches, in the list format the page has always used ── */}
      {showResults && (
      <>
      <p className="mb-2 text-sm text-gray-600 dark:text-slate-300">
        {isLoadingClients ? 'Searching…' : (
          <>
            <span className="font-semibold text-gray-900 dark:text-slate-100">{clientsTotal}</span>
            {` ${clientsTotal === 1 ? 'client matches' : 'clients match'} `}
            <span className="font-semibold text-gray-900 dark:text-slate-100">“{searchTerm}”</span>
            {statusFilter !== 'all' && (
              <>
                {' ('}
                {STATUS_FILTERS.find(o => o.value === statusFilter)?.label.toLowerCase()}
                {')'}
              </>
            )}
            {clientsTotal > 1 ? ' — open the right one.' : ''}
          </>
        )}
      </p>
      {/* Filter sits on the same line as the actions and the pager. */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center gap-2">
          <label className="text-sm text-gray-600 dark:text-slate-300 hidden sm:block" htmlFor="clientStatusFilter">Status</label>
          <select
            id="clientStatusFilter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="toolbar-control min-w-[150px] sm:min-w-[200px]"
          >
            {STATUS_FILTERS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-2 ml-auto justify-end">
          {canManageClients && (
            <Button
              onClick={() => {
                if (!aiEnabled) {
                  setAddClientMode('manual');
                  openModal();
                  return;
                }
                setShowAddModeChooser(true);
              }}
              disabled={!!clientLimitStatus?.reached}
              variant="primary"
              size="sm"
              className="toolbar-control"
              title={clientLimitStatus?.reached ? 'Reached client limit' : 'Add Client'}
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path d="M12 4v16m8-8H4" />
                </svg>
              }
            >
              {clientLimitStatus?.reached ? 'Reached client limit' : 'Add Client'}
            </Button>
          )}
          {canManageClients && (
            <Button
              onClick={() => exportToCsv('clients.csv', paginatedClients.map(c => ({
                ID: c.personal_info.id,
                Name: [c.personal_info.first_name,c.personal_info.middle_name,c.personal_info.last_name].filter(Boolean).join(' '),
                ID_Passport: c.personal_info.id_passport_no,
                Phone: c.personal_info.phone_number,
                Active: c.personal_info.is_active?'Yes':'No'
              }))) }
              variant="secondary"
              size="sm"
              className="toolbar-control"
            >
              Export CSV
            </Button>
          )}
          <Pager page={currentPage} pages={totalPages} onPage={setCurrentPage} />
        </div>
      </div>
      {/* Client cards (shared ResponsiveTable renders at all breakpoints) */}
      <div className="bg-white dark:bg-slate-900">
        <ResponsiveTable
          data={tableData}
          columns={tableColumns}
          keyField="id"
          isLoading={isLoadingClients}
          emptyMessage={statusFilter === 'all'
            ? `No client matches “${searchTerm}”. Check the ID/passport number, spelling, client code or loan code — or try a * wildcard.`
            : `No ${STATUS_FILTERS.find(o => o.value === statusFilter)?.label.toLowerCase()} client matches “${searchTerm}”. Try All statuses.`}
          headerClassName="bg-gray-50 dark:bg-slate-800"
          onRowClick={(row) => openClientWorkspace(row.clientData)}
        />
      </div>
      </>
      )}

      {/* ── 3. One client, open beside their loans ── */}
      {showWorkspace && (() => {
        const wsRow = buildClientRow(workspaceClient);
        return (
          <div className="space-y-3">
            <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500" />
              <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-slate-50 truncate">
                      {wsRow.fullName || '—'}
                    </h2>
                    {clientStatusBadge(wsRow)}
                  </div>
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-slate-400">
                    <span className="font-mono">{wsRow.client_code || '-'}</span>
                    {' · ID/Passport '}{wsRow.id_passport_no || '-'}
                    {' · '}{wsRow.phone_number || '-'}
                  </p>
                </div>
              </div>
              <div className="px-4 pb-3 pt-1 border-t border-gray-100 dark:border-slate-800">
                {renderActions(wsRow, { hideViewDetails: true })}
              </div>
            </div>

            {/* Documents — the client's identity documents only (ID and proof of
                residence). A loan's documents stay on the loan, in its details. */}
            {(() => {
              const clientDocs = clientOnlyDocs.filter((d) => isDocType(d, CLIENT_DOC_TYPES));
              const hasAny = clientDocs.length > 0;
              return (
                <section className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
                  <header className="px-4 py-2.5 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/60 flex flex-wrap items-center gap-x-2 gap-y-1">
                    <FileText size={15} className="text-blue-600 dark:text-blue-400" />
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100">Documents</h3>
                    <span className="text-xs text-gray-400 dark:text-slate-500">
                      ID and proof of residence
                    </span>
                  </header>
                  <div className="p-3">
                    <DocumentsStrip
                      documents={clientDocs}
                      loading={loadingTopDocs && !hasAny}
                      emptyMessage="No ID or proof of residence on file for this client."
                    />
                  </div>
                </section>
              );
            })()}

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 items-start">
              {/* Client */}
              <section className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
                <header className="px-4 py-2.5 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/60 flex items-center gap-2">
                  <User size={15} className="text-blue-600 dark:text-blue-400" />
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100">Client</h3>
                </header>
                <div className="p-4 form-compact">
                  {renderAccordionSections(true, detailsFormData(detailsClient), true)}
                </div>
              </section>

              {/* Loans */}
              <section className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
                <header className="px-4 py-2.5 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/60 flex items-center gap-2">
                  <CreditCard size={15} className="text-emerald-600 dark:text-emerald-400" />
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100">Loans</h3>
                </header>
                <div className="p-3">
                  {canViewLoans ? (
                    <ManageLoans
                      key={`loans-${detailsClient?.personal_info?.id}-${loansRefreshKey}`}
                      clientId={detailsClient?.personal_info?.id}
                      embedded
                    />
                  ) : (
                    <p className="py-10 text-center text-sm text-gray-400 dark:text-slate-500">
                      You do not have permission to view loans.
                    </p>
                  )}
                </div>
              </section>
            </div>

            {/* Everything that has happened to this client — their own events
                plus their loans', documents' and repayments'. Full width below
                both panels, since a diff needs the room. */}
            {detailsClient?.personal_info?.id && (
              <EntityAuditTrail
                scope="client"
                id={detailsClient.personal_info.id}
                title="Audit trail"
              />
            )}
          </div>
        );
      })()}
      </>
      )}

      {/* Add Client — capture mode chooser */}
      {showAddModeChooser && aiEnabled && (
        <FullScreenPanel
          isOpen
          onClose={() => setShowAddModeChooser(false)}
          title="Add Client"
          subtitle="How would you like to capture this client?"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => { setAddClientMode('ai'); setShowAddModeChooser(false); openModal(); }}
              className="group text-left rounded-xl border-2 border-blue-200 dark:border-slate-600 bg-white dark:bg-slate-800 p-5 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-slate-700/60 hover:shadow-md transition-all"
            >
              <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center mb-3">
                <Sparkles size={22} />
              </div>
              <p className="text-sm font-semibold text-gray-900 dark:text-slate-100">AI Auto-Fill</p>
              <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
                Upload the client's onboarding documents and let AI fill in the form. You review and complete anything missing.
              </p>
              <p className="mt-3 text-xs font-semibold text-blue-600 dark:text-blue-300 group-hover:underline">Start with documents →</p>
            </button>

            <button
              type="button"
              onClick={() => { setAddClientMode('manual'); setShowAddModeChooser(false); openModal(); }}
              className="group text-left rounded-xl border-2 border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 p-5 hover:border-emerald-400 dark:hover:border-emerald-500 hover:bg-emerald-50/40 dark:hover:bg-slate-700/60 hover:shadow-md transition-all"
            >
              <div className="w-11 h-11 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300 flex items-center justify-center mb-3">
                <PenLine size={22} />
              </div>
              <p className="text-sm font-semibold text-gray-900 dark:text-slate-100">Manual Entry</p>
              <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
                Type the client's details yourself, section by section. Documents can still be attached in the form.
              </p>
              <p className="mt-3 text-xs font-semibold text-emerald-600 dark:text-emerald-300 group-hover:underline">Start typing →</p>
            </button>
          </div>
        </FullScreenPanel>
      )}

      {/* Add Loan — capture mode chooser */}
      {showAddLoanModeChooser && aiEnabled && (
        <FullScreenPanel
          isOpen
          onClose={() => { setShowAddLoanModeChooser(false); setLoanClient(null); }}
          title="Add Loan"
          subtitle={`A new loan${loanClient?.personal_info?.first_name ? ` for ${[loanClient.personal_info.first_name, loanClient.personal_info.last_name].filter(Boolean).join(' ')}` : ''} starts from the client's documents`}
        >
          {loanDraft && (
            <div className="mb-4 rounded-xl border border-blue-200 dark:border-blue-800/50 bg-blue-50 dark:bg-blue-900/20 p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">You have a saved application for this client</p>
                <p className="text-xs text-blue-800 dark:text-blue-300 mt-0.5">
                  {loanDraft.principal_amount ? <>{toCurrency(loanDraft.principal_amount)} </> : null}
                  {(loanDraft.term_months || loanDraft.duration_months) ? <>over {loanDraft.term_months || loanDraft.duration_months} month(s) </> : null}
                  {loanDraft.saved_at ? <>· saved {formatDateDMY(loanDraft.saved_at)}</> : null}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={discardLoanDraft}
                  disabled={loanDraftBusy}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-slate-700 disabled:opacity-50"
                >
                  Discard
                </button>
                <button
                  type="button"
                  onClick={() => resumeLoanDraft()}
                  disabled={loanDraftBusy}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {loanDraftBusy ? 'Opening…' : 'Continue it'}
                </button>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 gap-4">
            <button
              type="button"
              onClick={() => {
                setAddLoanMode('ai');
                setAddLoanPhase('prequalify');
                setLoanAssessment(null);
                setLoanTermMonths(null);
                setLoanPreqKey(k => k + 1);
                setShowAddLoanModeChooser(false);
                setShowLoanModal(true);
              }}
              className="group text-left rounded-xl border-2 border-blue-200 dark:border-slate-600 bg-white dark:bg-slate-800 p-5 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-slate-700/60 hover:shadow-md transition-all"
            >
              <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center mb-3">
                <Sparkles size={22} />
              </div>
              <p className="text-sm font-semibold text-gray-900 dark:text-slate-100">AI Pre-Qualification</p>
              <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
                Upload the bank statement and payslip and let AI score affordability first. The loan form opens pre-filled with what the client qualifies for.
              </p>
              <p className="mt-3 text-xs font-semibold text-blue-600 dark:text-blue-300 group-hover:underline">Start with documents →</p>
            </button>
          </div>
        </FullScreenPanel>
      )}

      {/* Edit/Add Client Modal */}
      {showModal && (
        <FullScreenPanel
          isOpen
          onClose={() => setShowModal(false)}
          title={selectedClient ? 'Edit Client' : 'Add Client'}
          fullHeight
        >
          <div className="text-sm form-compact">
            {modalMessage && (
              <div className={`p-3 rounded-lg mb-5 text-sm ${getModalMessageClass()}`}>
                {modalMessage}
              </div>
            )}
            {selectedClient ? renderAccordionSections(false) : renderStepperForm()}
          </div>
        </FullScreenPanel>
      )}

      {/* Client Details Modal */}
      {showDetailsModal && detailsClient && (
        <FullScreenPanel
          isOpen
          onClose={() => setShowDetailsModal(false)}
          title="Client Details"
          subtitle={detailsClient.personal_info?.client_code || [detailsClient.personal_info?.first_name, detailsClient.personal_info?.last_name].filter(Boolean).join(' ')}
          fullHeight
        >
          <div>
              {renderAccordionSections(true, detailsFormData(detailsClient))}
          </div>
        </FullScreenPanel>
      )}

      {/* Add Loan Modal */}
      {showLoanModal && loanClient && (
        <FullScreenPanel
          isOpen
          onClose={closeAddLoanModal}
          title={`Add Loan for ${[loanClient.personal_info.first_name, loanClient.personal_info.middle_name, loanClient.personal_info.last_name].filter(Boolean).join(' ')}`}
          fullHeight
        >
              {/* Updated loan message display with proper error/success styling */}
              {loanMessage && (
                <div className={`mb-6 p-3 rounded-lg border flex items-center justify-between text-sm ${
                  loanMessage.includes('Failed') || loanMessage.includes('Error') || loanMessage.includes('error')
                    ? 'bg-red-50 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-700'
                    : 'bg-green-50 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-700'
                }`}>
                  <span>{loanMessage}</span>
                  <button
                    onClick={() => setLoanMessage('')}
                    className="ml-4 px-2 py-1 rounded-full text-lg font-bold hover:bg-gray-200 focus:outline-none"
                    aria-label="Close"
                    title="Close"
                  >
                    ×
                  </button>
                </div>
              )}
              
              {aiEnabled && addLoanMode === 'ai' && addLoanPhase === 'prequalify' ? (
                <PreQualification
                  key={loanPreqKey}
                  clientId={loanClient?.personal_info?.id}
                  heading="Loan Pre-Qualification"
                  onCompleted={handleLoanPrequalified}
                  onCancel={closeAddLoanModal}
                  initialAssessment={loanAssessment}
                  initialTermMonths={loanTermMonths}
                  onReviewSent={(message) => {
                    closeAddLoanModal();
                    setLoanClient(null);
                    setMessage(message);
                    setLoansRefreshKey(k => k + 1);
                    fetchClients();
                  }}
                />
              ) : (
              <form
                onSubmit={e => {
                  e.preventDefault();
                  handleAddLoan();
                }}
              >
                {aiEnabled && addLoanMode === 'ai' && (
                  <div className="mb-5 rounded-lg border border-emerald-200 dark:border-emerald-800/40 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <CheckCircle2 size={14} className="text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                      <p className="text-xs text-emerald-700 dark:text-emerald-300">
                        Pre-qualified · qualifies for up to <strong>{toCurrency(loanAssessment?.affordability?.recommended_amount)}</strong>
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={handleBackToLoanScore}
                        className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 hover:underline"
                      >
                        Back to score
                      </button>
                      <button
                        type="button"
                        onClick={handleRerunPrequalification}
                        className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 hover:underline"
                      >
                        Re-run pre-qualification
                      </button>
                    </div>
                  </div>
                )}
                <LoanForm
                  loanForm={loanForm}
                  setLoanForm={setLoanForm}
                  loanFormErrors={loanFormErrors}
                  interestRates={interestRates}
                  deductionDay={deductionDay}
                  setDeductionDay={setDeductionDay}
                  bankStatementFile={bankStatementFile}
                  setBankStatementFile={setBankStatementFile}
                  payslipFile={payslipFile}
                  setPayslipFile={setPayslipFile}
                  bureauFile={bureauFile}
                  setBureauFile={setBureauFile}
                  bureauDate={bureauDate}
                  setBureauDate={setBureauDate}
                  canAddLoan={canAddLoan}
                  showApplicationType={true}
                  hideDocuments={aiEnabled && addLoanMode === 'ai'}
                  expenseCodes={loanExpenseCodes}
                  setExpenseCodes={setLoanExpenseCodes}
                  creditAssessmentId={(aiEnabled && addLoanMode === 'ai' && loanAssessment?.id) || null}
                  clientId={loanClient?.personal_info?.id || null}
                />

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-5 border-t border-gray-100 dark:border-slate-700 mt-5">
                  <button
                    type="button"
                    onClick={closeAddLoanModal}
                    className="px-5 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveLoanForLater}
                    disabled={loanDraftBusy}
                    title="Keep what you have entered and finish this loan later"
                    className="px-5 py-2 rounded-lg border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 text-sm font-semibold hover:bg-blue-50 dark:hover:bg-slate-800 transition disabled:opacity-50"
                  >
                    {loanDraftBusy ? 'Saving…' : 'Save and finish later'}
                  </button>
                  <ActionButton
                    type="submit"
                    onClick={(e) => {
                      const form = e.currentTarget.form;
                      if (form && typeof form.reportValidity === 'function' && !form.reportValidity()) return;
                      e.preventDefault();
                      return handleAddLoan();
                    }}
                    busyLabel="Adding…"
                    className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!canAddLoan}
                  >
                    {canAddLoan ? 'Add Loan' : 'Cannot Add Loan'}
                  </ActionButton>
                </div>
              </form>
              )}
        </FullScreenPanel>
      )}

      {/* Contract Modal */}
      {showContractModal && loanClient && (
        <FullScreenPanel
          isOpen
          onClose={() => { setShowContractModal(false); window.__walkInPendingLoanId = null; }}
          title="Review Loan Agreement"
        >
              {/* Show error/success message */}
              {loanMessage && (
                <div className={`mb-8 p-4 rounded-lg border flex items-center justify-between ${
                  loanMessage.includes('Failed') || loanMessage.includes('Error') || loanMessage.includes('error')
                    ? 'bg-red-50 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-700'
                    : 'bg-green-50 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-700'
                }`}>
                  <span>{loanMessage}</span>
                  <button
                    onClick={() => setLoanMessage('')}
                    className="ml-4 px-2 py-1 rounded-full text-lg font-bold hover:bg-gray-200 focus:outline-none"
                    aria-label="Close"
                    title="Close"
                  >
                    ×
                  </button>
                </div>
              )}
              
              {/* Always show ContractPreview */}
              <ContractPreview
                loanData={{
                  ...loanForm,
                  id: loanForm.id,
                  // What the contract shows is the API's quote of the chosen
                  // expenses (the loan form copies it onto loanForm).
                  additional_expenses: loanForm.additional_expenses || [],
                }}
                clientData={{
                  id: loanClient.personal_info.id, // <-- add this line
                  first_name: loanClient.personal_info.first_name,
                  middle_name: loanClient.personal_info.middle_name,
                  last_name: loanClient.personal_info.last_name,
                  id_passport_no: loanClient.personal_info.id_passport_no,
                  email: loanClient.personal_info.email,
                  phone_number: loanClient.personal_info.phone_number,
                  postal_address: loanClient.personal_info.postal_address,
                  erf_no: loanClient.personal_info.erf_no,
                  street_name: loanClient.personal_info.street_name,
                  suburb: loanClient.personal_info.suburb,
                  city: loanClient.personal_info.city,
                  country: loanClient.personal_info.country,
                  residential_address: loanClient.personal_info.residential_address,
                  marital_status: loanClient.personal_info.marital_status,

                  // Occupation details
                  occupation: loanClient.occupation_info.occupation,
                  salary: loanClient.occupation_info.salary,
                  employer_name: loanClient.occupation_info.employer_name,
                  payslip_number: loanClient.occupation_info.payslip_number,
                  employer_address: loanClient.occupation_info.employer_address,
                  employer_phone: loanClient.occupation_info.employer_phone,

                  // Bank details
                  bank_name: loanClient.bank_info.bank_name,
                  account_number: loanClient.bank_info.account_number,
                  branch_code: loanClient.bank_info.branch_code,
                  account_type: loanClient.bank_info.account_type
                }}
                onEdit={handleBackToLoanForm}
                onGenerate={handleGenerateContract}
                loading={isGeneratingContract}
              />
        </FullScreenPanel>
      )}

      {/* In-page document-mismatch override prompt. Replaces window.confirm()
          so the dialog matches the rest of the app's styling. */}
      {mismatchOverridePrompt && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mismatch-override-title"
        >
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-amber-200 dark:border-amber-700/40">
            <div className="px-6 py-4 border-b border-amber-100 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-900/30 flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/60 flex items-center justify-center">
                <svg className="w-5 h-5 text-amber-700 dark:text-amber-300" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
              </div>
              <h3 id="mismatch-override-title" className="text-base font-semibold text-amber-900 dark:text-amber-100">
                Document mismatch detected
              </h3>
            </div>

            <div className="px-6 py-5 text-sm text-slate-700 dark:text-slate-200 space-y-3">
              <p>The uploaded documents do not match the client information you captured.</p>
              {mismatchOverridePrompt.mismatchLabels.length > 0 && (
                <p>
                  <span className="font-semibold">Mismatch detected in:</span>{' '}
                  {mismatchOverridePrompt.mismatchLabels.join(', ')}.
                </p>
              )}
              {mismatchOverridePrompt.reviewLabels.length > 0 && (
                <p>
                  <span className="font-semibold">Review required for:</span>{' '}
                  {mismatchOverridePrompt.reviewLabels.join(', ')}.
                </p>
              )}
              {!mismatchOverridePrompt.mismatchLabels.length && !mismatchOverridePrompt.reviewLabels.length && (
                <p>{mismatchOverridePrompt.summary}</p>
              )}
              <p className="pt-1 font-semibold text-slate-900 dark:text-slate-100">
                Do you still want to add this client with the mismatched information?
              </p>
            </div>

            <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900/40 border-t border-slate-100 dark:border-slate-700/60 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setMismatchOverridePrompt(null)}
                disabled={isAddingClient}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-600 disabled:opacity-60"
              >
                Cancel
              </button>
              <ActionButton
                type="button"
                onClick={async () => {
                  setMismatchOverridePrompt(null);
                  await handleAddClient({ forceSubmit: true });
                }}
                disabled={isAddingClient}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-60 shadow-sm"
              >
                {isAddingClient ? 'Adding…' : 'Add anyway'}
              </ActionButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageClients;